// Minimal EN/ES dictionary (Cuba launch languages)
import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { lsGet, lsSet } from "./safeStorage";

const dict = {
  en: {
    app_name: "Habano",
    tagline: "The directory & companion for cigar lounges, vendors, and travelers in Havana.",
    continue_google: "Continue with Google",
    splash_eyebrow: "Havana · Established 2025",
    splash_caption: "Built for the streets of La Habana — works on slow networks, works offline.",
    select_role_title: "Choose your account",
    select_role_caption: "You can change this later. Each path is tailored differently.",
    traveler_title: "Traveler · Enthusiast",
    traveler_body: "Find the city's finest lounges, check in, log your journey, and earn the local's nod.",
    vendor_title: "Vendor",
    vendor_body: "Verified street-level vendors. Get discovered, take WhatsApp leads, stay reactivated.",
    business_title: "Business",
    business_body: "Lounges, bars, restaurants — claim your profile, manage VIP, surface in search.",
    continue: "Continue",
    welcome: "Welcome",
    your_role: "Role",
    signed_in_as: "Signed in as",
    logout: "Log Out",
    foundation_title: "Foundation status",
    foundation_caption: "Six non-negotiable rules. Verified live against the database.",
    upload_title: "Image pipeline",
    upload_caption: "Drop an image. We generate three sizes and lazy-load.",
    drop_here: "Drop image, or click to choose",
    uploading: "Compressing & uploading…",
    thumbnail: "Thumbnail (150px)",
    medium: "Medium (600px)",
    full: "Full (1200px)",
    run_test: "Run test",
    home: "Home",
    foundation: "Foundation",
    pipeline: "Pipeline",
    privacy: "Privacy",
    terms: "Terms",

    // Home screen
    home_editorial: "Editorial",
    home_tonight_in: "Tonight in",
    home_havana: "Havana",
    home_see_all: "See all →",
    home_search_placeholder: "Search lounges, vendors, vibes…",
    home_recently_visited_eyebrow: "Continue your trip",
    home_recently_title: "Recently",
    home_recently_accent: "visited",
    home_discover: "Discover",
    home_nearby_places: "Nearby places",
    home_all_places: "All places",
    home_no_picks: "No featured picks tonight. Check back after sundown.",
    home_no_match: "No places match your filters.",

    // Category chips
    chip_all: "All",
    chip_lounges: "Cigar Lounge",
    chip_cigar_shops: "Cigar Shop",
    chip_restaurants: "Restaurant",
    chip_rooftops: "Rooftop",
    chip_nightlife: "Nightlife",
    chip_hotels: "Hotel",
    chip_beaches: "Beach",
    chip_bar: "Bar",
    chip_local_gem: "Local Gem",

    // iter133 — Shared Filter Places sheet
    filter_places_title: "Filter Places",
    filter_section_categories: "Categories",
    filter_open_now: "Open Now",
    filter_open_now_sub: "Show only places currently open",
    filter_vip_only: "VIP Only",
    filter_vip_only_sub: "Show only VIP-enabled venues",
    filter_cigar_friendly: "Cigar Friendly",
    filter_cigar_friendly_sub: "Show only smoking-allowed venues",
    filter_distance: "Distance",
    filter_distance_near: "Nearby (<1 km)",
    filter_distance_walking: "Walking (1–3 km)",
    filter_any: "Any",
    filter_min_rating: "Minimum Rating",
    filter_rating_4plus: "4+ Stars",
    filter_rating_3plus: "3+ Stars",
    filter_sort_by: "Sort By",
    filter_sort_nearest: "Nearest",
    filter_sort_rating: "Highest Rated",
    filter_sort_popular: "Most Popular",
    filter_sort_recent: "Recently Added",
    filter_clear_all: "Clear all",
    filter_show_results: "Show {n} results",
    map_places_nearby: "{n} places nearby",
    map_place_nearby_one: "{n} place nearby",
    map_search_placeholder: "Search places…",

    // Exchange rate
    exch_eyebrow: "Live data · elToque · Mercado Informal",
    exch_title: "Cuba Exchange Rates",
    exch_official_rates: "Official rates",
    exch_segmento_iii: "Informal Market",
    exch_source_attribution: "Source: elToque · Mercado Informal",
    exch_explainer_title: "What is the Informal Market rate?",
    exch_explainer_body: "This is the informal-market USD/EUR ↔ CUP rate Cubans actually use day-to-day. Published by elToque from real reported transactions — typically far above the official Banco Central de Cuba rate.",
    exch_updated: "Updated",
    exch_estimate_only: "Estimate only — informal market reference",
    exch_stale_msg: "Rate not updated recently — use as a guide only.",
    exch_offline_title: "Last available rate",
    exch_history: "30-day history",

    // Weather
    weather_havana: "Havana Weather",
    weather_current_conditions: "Current Conditions",
    weather_next_24: "Next 24 Hours",
    weather_hourly: "Hourly Forecast",
    weather_7day: "7-Day Forecast",
    weather_morning: "Morning",
    weather_afternoon: "Afternoon",
    weather_evening: "Evening",
    weather_rain_prob: "Rain prob.",
    weather_unit: "Temperature unit",
    weather_feels_like: "Feels like",
    weather_humidity: "Humidity",
    weather_wind: "Wind",
    weather_uv: "UV Index",
    weather_visibility: "Visibility",
    weather_sunrise: "Sunrise",
    weather_sunset: "Sunset",
    weather_now: "Now",
    weather_last_updated: "Last updated",
    weather_stale: "Weather not updated recently",
    weather_tip_hot: "Hot day ahead — stay hydrated and plan indoor visits during midday hours.",
    weather_tip_rain: "Rain expected — consider indoor lounges and cigar bars tonight.",
    weather_tip_uv: "High UV today — bring sun protection if spending time outdoors.",
    weather_tip_cool: "Cool evening ahead — perfect weather for a rooftop with a Habano.",
    weather_tip_perfect: "Perfect Havana evening — great night for a rooftop or outdoor lounge.",

    // Onboarding
    onb_tagline_1: "The only app you need",
    onb_tagline_2: "for",
    onb_tagline_cuba: "Cuba.",
    onb_subtitle: "Built for travelers. Rooted in cigar culture.",
    onb_offline: "Works even with no internet connection",
    onb_check_1: "Know the current exchange rate",
    onb_check_2: "Get transportation anywhere in Cuba",
    onb_check_3: "Discover cigar lounges, restaurants, rooftop bars, and exclusive venues",
    onb_check_4: "Access VIP services and curated experiences",
    onb_check_5: "Save favorite locations and custom destinations — even offline",
    onb_whatsapp_required: "WhatsApp required",
    onb_whatsapp_body: "All communication with transportation providers and businesses is handled through WhatsApp. Please make sure it is installed and working.",
    onb_explore: "Explore",
    onb_explore_sub: "Lounges, restaurants, venues",
    onb_explore_cta: "Tap to Explore →",
    onb_transport: "Transportation",
    onb_transport_sub: "Airport, city rides",
    onb_transport_cta: "Get a Ride →",
    onb_vip: "VIP",
    onb_vip_sub: "Premium experiences",
    onb_vip_cta: "Go VIP →",
    onb_sim_title: "Get a SIM or eSIM for Cuba",
    onb_sim_sub: "Stay connected — SIM and eSIM options for travelers",
    onb_skip: "Skip — Take Me to the App",
    onb_focus: "Focus on enjoying your trip, not figuring it out.",
    onb_legal_prefix: "By continuing you agree to our",
    onb_legal_terms: "Terms",
    onb_legal_and: "&",
    onb_legal_privacy: "Privacy",
    /* ---- Profile menu + Journey + Settings (iter 50 i18n retrofit) ---- */
    menu_my_profile: "My Profile",
    menu_my_saved: "My Saved",
    menu_just_for_you: "Just for You",
    menu_my_journey: "My Journey",
    menu_notifications: "Notifications",
    menu_friends: "Friends",
    menu_my_qr: "My QR Code",
    menu_settings: "Settings",
    menu_privacy_settings: "Privacy Settings",
    menu_open_support: "Open a Support Request",
    menu_business_dashboard: "Business Dashboard",
    menu_vendor_dashboard: "Vendor Dashboard",
    menu_admin_dashboard: "Admin Dashboard",
    menu_sign_out: "Log Out",
    menu_my_tickets: "My Tickets",
    menu_my_dashboard: "My Dashboard",
    // Location permission priming
    loc_map_headline: "See Havana around you",
    loc_map_body: "Allow location access to see nearby cigar lounges, restaurants, rooftops, and venues with distances and walking times.",
    loc_map_secondary: "Not now — browse without location",
    loc_ride_headline: "Share your location for pickup",
    loc_ride_body: "Allow location access so your driver knows exactly where to pick you up in Havana.",
    loc_ride_secondary: "Not now — enter pickup manually",
    loc_airport_headline: "Find you at the airport",
    loc_airport_body: "Allow location so we can confirm you are at José Martí International Airport and send the right driver to your terminal.",
    loc_airport_secondary: "Not now — I will describe my location",
    loc_navigate_headline: "Get directions from where you are",
    loc_navigate_body: "Allow location access to get walking directions from your current position to this venue.",
    loc_navigate_secondary: "Not now — open maps without my location",
    loc_distance_headline: "See how far you are",
    loc_distance_body: "Allow location access to see distances and walking times to every venue in Havana.",
    loc_distance_secondary: "Not now",
    loc_cta_enable: "Enable Location",
    loc_denied_toast: "Location access was denied — you can enable it in your phone Settings",
    loc_denied_ios_hint: "On iPhone: Settings → Safari → Location → Allow",
    loc_denied_android_hint: "On Android: Settings → Site Settings → Location → Allow",
    loc_denied_revisit: "To enable location go to your browser Settings and allow location for habano.app",
    settings_reset_location_label: "Reset Location Preferences",
    settings_reset_location_hint: "This will allow Habano to ask for your location permission again",
    settings_reset_location_confirm_title: "Reset Location Preferences?",
    settings_reset_location_confirm_body: "This will reset your location preferences. You will be asked to enable location access again the next time you use the map or request a ride.",
    settings_reset_location_confirm_cta: "Reset Preferences",
    settings_reset_location_cancel: "Cancel",
    settings_reset_location_toast: "Location preferences reset — you will be prompted again next time",
    // iter62 — Friend profile
    friend_connected_toast: "You and {name} are now connected on Habano",
    friend_profile_back: "Back to friends",
    friend_profile_staying_at: "Staying at",
    // iter65 — own profile
    me_profile_title: "My Profile",
    me_profile_edit: "Edit Profile",
    me_profile_save: "Save",
    me_profile_cancel: "Cancel",
    me_profile_field_name: "Display name",
    me_profile_field_photo: "Profile photo",
    me_profile_field_instagram: "Instagram",
    me_profile_field_instagram_hint: "@ is stripped automatically",
    me_profile_field_photo_change: "Change photo",
    me_profile_field_photo_upload: "Upload photo",
    me_profile_saved_toast: "Profile updated",
    me_profile_loading: "Loading profile…",
    me_profile_share: "Share my profile",
    me_profile_share_copied: "Profile link copied",
    // iter66 — bell dropdown
    bell_see_all: "See all",
    bell_empty: "You're all caught up",
    bell_dismiss: "Dismiss",
    friend_profile_favorites_suffix: "'s Favorite Spots",
    friend_profile_saved_suffix: "'s Saved Spots",
    friend_profile_see_all: "See all",
    friend_profile_unfriend: "Unfriend {name}",
    friend_profile_unfriend_confirm: "Are you sure you want to unfriend {name}? This will remove all shared information between you.",
    friend_profile_action_whatsapp: "WhatsApp",
    friend_profile_action_ride: "Request Ride to their location",
    friend_profile_action_navigate: "Navigate to their location",
    friend_profile_action_instagram: "View on Instagram",
    friend_profile_member_since: "Member since",
    friend_profile_badge_explorer: "Explorer",
    friend_profile_badge_enthusiast: "Enthusiast",
    friend_profile_badge_connoisseur: "Connoisseur",
    friend_profile_connected: "You are now connected",
    friend_profile_loading: "Loading friend profile…",
    friend_profile_not_found: "Could not load this friend's profile.",
    // iter63 — QR scanner page
    scan_card_headline: "Find a Habano QR",
    scan_card_sub: "Business, vendor, friend, or admin onboarding code — we'll know which.",
    scan_card_cta: "Tap to Start Scanning",
    scan_show_my_qr: "Show My QR",
    scan_more: "MORE",
    scan_save_a_location: "Save a Location",
    scan_my_friends: "My Friends",
    scan_saved_places: "Saved Places",
    scan_your_favorites: "Your favorites",
    scan_no_camera_label: "NO CAMERA? PASTE A LINK",
    nav_scan_label: "SCAN",
    // iter64 — Notifications + signup polish
    notif_group_today: "Today",
    notif_group_yesterday: "Yesterday",
    notif_group_earlier: "Earlier",
    notif_empty_caught_up: "You are all caught up",
    notif_detail_close: "Close",
    notif_dismiss_aria: "Dismiss",
    traveler_signup_skip: "Skip this step",
    traveler_signup_skip_instagram: "Skip — I'll add this later",
    journey_eyebrow_passport: "Your passport",
    journey_havana_updates: "Havana Updates",
    journey_havana_updates_sub: "Latest travel alerts and important information",
    journey_survival_guide: "Havana Survival Guide",
    journey_survival_guide_sub: "Everything you need to know",
    journey_traveler_tools: "Traveler tools",
    journey_travel_tips: "Travel Tips",
    journey_get_sim: "Get a SIM or eSIM for Cuba",
    journey_continue_trip: "Continue your trip",
    journey_recently_visited: "Recently visited",
    journey_my_places: "My Places",
    journey_quick_actions: "Quick actions",
    journey_add_location: "Add a Location",
    journey_no_places: "No places saved yet",
    journey_no_places_sub: "Save businesses you visit to build your Havana journey.",
    journey_sign_in_backup: "Sign in to back up your saved locations to your account",
    journey_total_spent: "Total spent",
    journey_hire_guide: "Hire a Tour Guide",
    journey_hotel: "Hotel",
    journey_airport: "Airport",
    journey_emergency: "Emergency",
    journey_vip: "VIP",
    journey_support: "Support",
    onb_headline_part1: "The only app you need for",
    onb_whatsapp_short: "Needed for rides and business contacts.",
    onb_sim_sub_short: "Stay connected. Easy setup.",
    onb_sim_esim_badge: "eSIM",
    onb_cta_main: "Take Me to the App",
    onb_footer_secure: "Secure. Private. Trusted.",

    // Journey
    journey_title: "My Journey",
    journey_quick_actions: "Quick actions",
    journey_traveler_tools: "Traveler tools",
    journey_check_ins: "Check-ins",
    journey_saved: "Saved places",
    journey_recently_visited: "Recently visited",

    // Airport
    airport_title: "Airport · Transportation",
    airport_book_ride: "Book a ride",
    airport_arrival: "Arrival pickup",
    airport_departure: "Departure",
    airport_tips: "Airport tips",

    // Notifications
    notif_welcome: "Welcome to Habano",
    notif_urgent: "Urgent update",
    notif_customs: "Customs notice",
    notif_currency: "Currency update",
    notif_event: "Event tonight",
    notif_empty: "No notifications yet.",

    // Settings — legal block
    settings_legal_eyebrow: "Legal",
    settings_privacy_policy: "Privacy Policy",
    settings_privacy_hint: "How we handle your data",
    settings_terms_service: "Terms of Service",
    settings_terms_hint: "Rules of using Habano",
    settings_terms_accepted_prefix: "Terms & Privacy accepted on",
    settings_language: "Language",
    settings_language_label: "App language",
    settings_language_hint: "Changes apply instantly across the app.",
    map_filter_saved: "Saved",
    map_filter_open_now: "Open Now",
    map_filter_vip: "VIP",
    map_filter_food: "Food",
    map_filter_car: "Ride",
    // iter150 — Category Menu sheet (opened from bottom bar Menu button).
    map_filter_menu: "Menu",
    map_menu_clear_all: "Clear all",
    map_menu_title: "Categories",
    map_menu_search_muted: "Search",
    map_menu_search_active: "Search · {n} places",
    map_menu_search_active_1: "Search · 1 place",
    // Category display names — used both in the sheet and the map chips.
    map_cat_food_en: "Food",           map_cat_food_es: "Comida",
    map_cat_bar_en: "Bar",             map_cat_bar_es: "Bar",
    map_cat_cafe_en: "Café",           map_cat_cafe_es: "Café",
    map_cat_cigar_en: "Cigar",         map_cat_cigar_es: "Tabaco",
    map_cat_nightlife_en: "Nightlife", map_cat_nightlife_es: "Vida Nocturna",
    map_cat_rooftop_en: "Rooftop",     map_cat_rooftop_es: "Azotea",
    map_cat_pool_en: "Pool",           map_cat_pool_es: "Piscina",
    map_cat_tours_en: "Tours",         map_cat_tours_es: "Tours",
    map_cat_activities_en: "Activities", map_cat_activities_es: "Actividades",
    map_cat_market_en: "Market",       map_cat_market_es: "Mercado",
    map_cat_tienda_en: "Tienda",       map_cat_tienda_es: "Tienda",
    map_cat_beauty_en: "Beauty",       map_cat_beauty_es: "Belleza",
    map_no_open_food: "No open restaurants nearby",
    map_no_open_now: "Nothing open nearby right now",
    map_filter_spots: "My Places",
    map_recenter: "Recenter on me",
    map_location_unavailable: "Location not available — enable location access in your settings",
    chip_local_gem: "Local Gem",
    cl_form_new_title: "Save a Location",
    // iter67 — minimal save-a-location flow
    sal_gps_capturing: "Getting your location...",
    sal_gps_captured: "Location captured",
    sal_gps_tap: "Tap to get location",
    sal_name_label: "NAME",
    sal_name_placeholder: "What is this place?",
    sal_desc_label: "DESCRIPTION",
    sal_desc_placeholder: "A quick note about this place",
    sal_whatsapp_label: "WHATSAPP",
    sal_whatsapp_placeholder: "+53 5 555 5555",
    sal_whatsapp_hint: "Cuba number",
    sal_save_btn: "Save Location",
    sal_only_required: "Name is the only required field",
    sal_photo_title: "Add a Photo?",
    sal_photo_sub: "Help others recognize this place when you share it",
    sal_photo_btn: "Add Photo",
    sal_back_discover: "Back to Discover",
    sal_request_ride: "Request a Ride",
    sal_share_location: "Share this Location",
    sal_upgrade_q: "Is this a business?",
    sal_upgrade_sub: "Add category, phone, address and more",
    sal_share_msg: "Check out this spot I saved in Havana — {link}",
    // iter130 — Interactive crop + Back to Home
    sal_back_home: "Back to Home",
    sal_photo_crop_hint: "Drag and pinch to adjust",
    sal_photo_save_btn: "Done",
    // iter68 — Journey dashboard rebuild
    jd_eyebrow: "MY JOURNEY",
    jd_title_a: "Your",
    jd_title_b: "Havana",
    jd_stat_saved: "SAVED",
    jd_stat_friends: "FRIENDS",
    jd_stat_spots: "MY PLACES",
    jd_staying_at: "STAYING AT",
    jd_save_hotel_t: "Save My Hotel or Airbnb",
    jd_save_hotel_s: "Get distances and ride home easily",
    jd_offers_t: "OFFERS FOR YOU",
    jd_my_vendors: "MY VENDORS",
    jd_vip_reminders: "VIP REMINDERS",
    jd_see_all: "See all",
    jd_share_app: "Share Habano with a friend",
    jd_download_app: "Download Habano",
    jd_travel_tips: "Travel Tips",
    jd_hotel_fallback: "My Hotel",
    jd_share_msg: "Check out Habano — your concierge for Havana. https://habano.app",
    jd_vip_removed: "Removed from VIP reminders",
    jd_undo: "Undo",
    // iter69 — Place profile copy
    pp_contact_wa: "Contact on WhatsApp",
    pp_contact_call: "Contact",
    pp_action_ride: "Request Ride",
    pp_action_maps: "Open in Maps",
    pp_action_share: "Share",
    pp_action_vip: "VIP Reminder",
    pp_action_spend: "Log Spend",
    pp_vip_added: "Added to VIP Reminders",
    pp_vip_removed: "Removed from VIP Reminders",
    pp_label_address: "ADDRESS",
    pp_label_phone: "PHONE",
    pp_action_copy: "Copy",
    pp_action_call: "Call",
    pp_action_chat: "Chat",
    pp_action_view: "View",
    pp_action_open: "Open",
    pp_copied: "Copied to clipboard",
    pp_hours_open: "Open Now",
    pp_hours_closed: "Closed",
    pp_hours_all: "All hours",
    pp_hours_closed_short: "Closed",
    pp_nearby_prefix: "Nearby",
    pp_nearby_see_all: "See all",
    pp_hours_unavailable: "Hours not available",
    // iter71 — Journey rebuild
    j71_qa_airport_t: "Airport",
    j71_qa_airport_s: "Rides & VIP",
    j71_qa_emergency_t: "Emergency",
    j71_qa_emergency_s: "Numbers & help",
    j71_qa_tips_t: "Tips",
    j71_qa_tips_s: "Arriving & leaving",
    j71_vip_btn: "VIP Reminders",
    j71_vip_sub: "{n} businesses saved",
    j71_vendors_btn: "My Vendors",
    j71_vendors_sub: "{n} saved · drivers, guides & more",
    j71_tonight_picks: "TONIGHT'S PICKS",
    j71_recent_favs: "RECENT FAVORITES",
    j71_saved_badge: "Saved",
    j71_add_home_t: "Add to Home Screen",
    j71_add_home_s: "Access without a browser",
    j71_offline_t: "Download for Offline",
    j71_offline_s: "Works with no internet in Cuba",
    j71_offline_t_v2: "Save App for Offline Use",
    j71_offline_s_v2: "Download the app so it works without internet in Cuba",
    j71_offline_loading: "Saving Havana…",
    j71_offline_done_label: "Saved for Offline",
    j71_offline_done_toast: "App saved — works without internet now",
    save_spot_cta: "Save This Location",
    save_spot_cta_finding: "Finding your location…",
    save_spot_err_perm: "Enable location to use this feature",
    save_spot_err_timeout: "Could not get your location — try again",
    journey_app_settings: "APP SETTINGS",
    j_card_save_phone_t: "Save to Phone",
    j_card_save_phone_s: "Install on your home screen",
    j_card_save_offline_t: "Save Offline",
    j_card_save_offline_s: "Works without internet in Cuba",
    j_card_save_offline_saving_t: "Saving…",
    j_card_save_offline_saving_s: "Downloading Havana map",
    j_card_save_offline_done_t: "Offline Ready",
    j_card_save_offline_saved_today: "Saved today",
    j_card_save_offline_saved_on: "Saved {date}",
    j_card_save_offline_update: "Update",
    j_share_btn: "Share Habano with a Friend",
    j_clear_cache_title: "Clear offline cache?",
    j_clear_cache_body: "This removes the saved app from your device. You can re-download anytime on a good connection.",
    j_clear_cache_confirm: "Clear Cache",
    j_clear_cache_cancel: "Keep It",
    j71_offline_progress: "Downloading Havana...",
    j71_offline_done: "Havana is ready offline ✓",
    j71_offline_uptodate: "Havana is up to date ✓",
    j71_offline_label: "Downloading Havana...",
    j71_offline_step_biz: "Caching businesses",
    j71_offline_step_photos: "Caching photos",
    j71_offline_step_fx: "Saving exchange rate",
    j71_offline_step_tips: "Saving travel tips",
    j71_offline_done_btn: "Done",
    j71_empty_t: "Start exploring Havana",
    j71_empty_s: "Save businesses and locations to build your personal Havana guide",
    j71_empty_cta: "Discover Places",
    j71_share_t: "Share Habano",
    j71_share_s: "Share with a friend",
    cl_form_edit_title: "Edit Location",
    cl_form_local_gem_note: "All custom locations start as Local Gem.",
    cl_form_photos: "Photos",
    cl_form_add_photo: "Add photo",
    cl_form_basics: "Basics",
    cl_form_location: "Location",
    cl_form_contact: "Contact",
    cl_form_visiting: "Visiting",
    cl_form_my_notes: "My Notes",
    cl_form_my_notes_help: "Private notes — visible only to you.",
    cl_form_name: "Name",
    cl_form_description: "Description",
    cl_form_address: "Address",
    cl_form_phone: "Phone",
    cl_form_whatsapp: "WhatsApp",
    cl_form_instagram: "Instagram",
    cl_form_website: "Website",
    cl_form_hours: "Hours",
    cl_form_smoking_policy: "Smoking policy",
    cl_form_optional: "optional",
    cl_form_request_directory: "Request this location be added to the Habano directory",
    cl_form_request_directory_help: "Our team will review and add it to the directory if approved.",
    cl_form_save_btn: "Save Location",
    add_location_btn: "Add a Location",
    tour_guide_interests: "Tour interests (optional)",
    tour_guide_name: "Your name",
    tour_guide_special: "Special requests (optional)",
    tour_guide_pickup: "Pickup location (optional)",
    tour_guide_pickup_placeholder: "Where should we pick you up?",
    support_button: "Support",
    support_headline: "How can we help?",
    support_subtext: "Tell us what happened and we will get back to you through WhatsApp.",
    support_name: "Name",
    support_email: "Email",
    support_category: "Category",
    support_message: "Message",
    support_message_placeholder: "Please describe what happened",
    support_screenshot: "Screenshot (optional)",
    support_screenshot_choose: "Attach a screenshot",
    support_screenshot_change: "Change screenshot",
    support_screenshot_fail: "Couldn't process that image — try a smaller one.",
    support_submit: "Submit support request",
    support_submitting: "Submitting…",
    support_missing_fields: "Please fill in name, email, and a message (at least 4 characters).",
    support_ticket_label: "Ticket ID",
    support_confirm_with_wa: "Your support request has been submitted. We will respond through WhatsApp shortly.",
    support_confirm_no_wa: "Your request has been submitted. Our team will contact you by email.",
    support_done: "Done",
    connected_eyebrow: "Stay Connected",
    connected_title: "Stay Connected in Cuba",
    connected_subtitle: "Choose the right option for your phone.",
    connected_sim_card: "My phone has a SIM slot",
    connected_esim_only: "My phone is eSIM only",
    connected_esim_only_sub: "iPhone 14 and newer US models",
    connected_sim_label: "Physical SIM Card",
    connected_sim_headline: "Buy at the Airport on Arrival",
    connected_sim_step_1: "Look for the ETECSA desk next to the baggage claim area in the arrivals hall — staff there can help you purchase a Cubacel tourist SIM card and get it set up.",
    connected_sim_step_2: "Load data immediately while you still have airport connectivity.",
    connected_sim_step_3: "Keep the SIM packaging — you may need the number for WhatsApp.",
    connected_sim_warning: "Buy your SIM before you leave the arrivals hall. Once you exit the airport it is harder to find.",
    connected_sim_cost_label: "Cost",
    connected_sim_cost: "Tourist SIM cards typically cost a few USD — prices may vary.",
    connected_sim_compat_label: "Compatibility",
    connected_sim_compat: "Check that your phone is unlocked before traveling — a locked phone cannot use a Cuban SIM.",
    connected_esim_label: "eSIM Providers for Cuba",
    connected_esim_critical_strong: "Install your eSIM BEFORE you leave home",
    connected_esim_critical_body: "you need wifi to install it and you may not have connectivity when you land.",
    connected_get_esim: "Get eSIM →",
    connected_esim_note_label: "Note",
    connected_esim_note: "All eSIMs are data only — no local Cuban phone number. You can still use WhatsApp calls and messages over data.",
    connected_esim_tip_label: "eSIM check",
    connected_esim_tip: "Not sure if your phone supports eSIM? Go to Settings → General → About and look for Carrier Lock or eSIM options.",
    connected_tips_label: "Good to Know",
    connected_tip_1: "Cuba connectivity is managed entirely by ETECSA — the state telecom provider.",
    connected_tip_2: "Data speeds can be slow or intermittent — download Habano offline before you arrive.",
    connected_tip_3: "Airport wifi is available in the arrivals hall — use it to install your eSIM and download offline data.",
    connected_tip_4: "Most hotels and Airbnbs have wifi — ask your host for the password on arrival.",
    connected_download_offline: "Download Habano Offline",
    connected_view_tips: "View Travel Tips",

    // Generic
    back: "Back",
    go_to_app: "Go to App",
    cancel: "Cancel",
    save: "Save",
    error_generic: "Something went wrong. Please try again.",
    offline_banner: "You're offline — showing cached data.",
    listing_inactive_warning: "This listing may no longer be active — contact them directly to confirm.",

    // ---- Traveler app i18n retrofit (Journey, Settings, ProfileMenu, Notifications, modals) ----
    loading_ellipsis: "Loading…",
    removed_placeholder: "(removed)",
    nav_discover: "Discover",
    nav_map: "Map",
    nav_scan: "Scan",
    nav_qr: "QR",
    nav_journey: "Journey",
    nav_profile: "Profile",
    nav_car: "Car",
    nav_ride: "Ride",

    // ---- Transportation Hub (/car) ----
    car_eyebrow: "TRANSPORTATION",
    car_where: "Where",
    car_to: "to?",
    car_search_ph: "Search your saved places, hotel, custom places…",
    car_map_search_ph: "Search Havana...",
    car_map_search_clear: "Clear",
    car_map_search_no_results: "No matches",
    car_map_search_hotel: "My Hotel",
    car_map_search_section_saved: "Saved",
    car_map_search_section_places: "Places",
    car_map_search_section_geocode: "Havana",
    car_staying_at: "STAYING AT",
    car_saved_places: "SAVED PLACES",
    car_recent_rides: "RECENT RIDES",
    car_find_on_map: "Find on Map",
    car_find_on_map_sub: "Drag and pin any location in Havana",
    car_save_hotel_prompt: "Save your hotel to request a ride home",
    car_save_hotel_cta: "Save my hotel",
    car_no_saved_places: "No saved places yet — save businesses or add custom places to see them here.",

    // ---- Find on Map (/car/map) ----
    car_map_title: "Find on Map",
    car_map_sub: "Drag map to choose your destination",
    car_map_panel_drag: "Drag the map to move the pin",
    car_map_panel_cta: "Get a Ride Here",
    // iter153 — Pickup location field on the ride popup.
    ride_pickup_label: "Pick up at",
    ride_pickup_loading: "Detecting your location...",
    ride_pickup_unavailable: "Location unavailable",
    ride_pickup_change: "Change →",
    ride_pickup_set_here: "Set Pickup Here",
    car_map_panel_hint: "Opens ride request with this location",
    car_map_change: "Change",
    car_map_loading: "Finding location…",
    car_map_offline_coords: "Offline — using coordinates",
    car_map_no_token: "Map unavailable — Mapbox token missing.",
    car_map_search_results: "Tap a spot below to drop the pin",
    car_map_from_airport_title: "Where in Havana?",
    car_map_from_airport_sub: "Drop a pin for your destination — pickup is locked to José Martí Airport.",

    journey_tab_visits: "Visits",
    journey_tab_my_places: "My Places",
    journey_tab_vendors: "Vendors",
    journey_tab_favorites: "Favorites",
    journey_tab_spend: "Spend",
    journey_heading_my_havana: "My Havana",
    journey_heading_journey: "journey",
    journey_subtitle: "Every check-in, every vendor, every favorite — stitched together. Yours alone, unless you decide to share it.",
    journey_stamp_visits: "Visits",
    journey_stamp_vendors: "Vendors",
    journey_stamp_favs: "Favs",
    journey_save_hotel_cta: "Tap here to save your hotel when you arrive",
    journey_save_badge: "Save",
    journey_survival_sub_short: "Top 10 things to know arriving or departing",
    journey_tool_airport: "Airport",
    journey_tool_tips: "Travel Tips",
    journey_sim_sub_alt: "Stay connected — SIM and eSIM options for travelers",
    // iter65 — Journey hub redesign
    hub_staying_at: "STAYING AT",
    hub_just_for_you: "JUST FOR YOU",
    hub_airport_label: "Airport",
    hub_airport_desc: "VIP service, rides, and arrival help",
    hub_sim_label: "Get a SIM or eSIM",
    hub_sim_desc: "Stay connected in Cuba",
    hub_saved_label: "My Saved Places",
    hub_saved_desc: "Your favorite businesses and locations",
    hub_tips_label: "Travel Tips",
    hub_tips_desc: "Arriving, in Cuba, leaving, and help",
    // Airport page rebuild
    airport_eyebrow: "AIRPORT",
    airport_title: "José Martí International Airport",
    airport_subtitle: "Terminal 2 · International Flights",
    airport_vip_label: "VIP Airport Service",
    airport_vip_desc: "Private transfer, meet and greet, luggage assistance — no negotiation needed",
    airport_vip_cta: "Request VIP Service",
    airport_arriving_label: "Arriving — Ride from the Airport",
    airport_arriving_desc: "Request a ride from José Martí Airport to your destination",
    airport_departing_label: "Departing — Ride to the Airport",
    airport_departing_desc: "Request a ride to José Martí Airport from your location",
    airport_negotiate_warn: "Negotiate the price before getting in the car",
    airport_negotiate_note: "Price is negotiated directly with your driver via WhatsApp",
    airport_arriving_cta: "Request Arrival Ride",
    airport_departing_cta: "Request Departure Ride",
    // Tips page rebuild — 4 tabs
    tips_eyebrow: "TRAVEL TIPS",
    tips_title: "Havana Travel Guide",
    tips_tab_in_cuba: "In Cuba",
    tips_tab_arriving: "Arriving",
    tips_tab_leaving: "Leaving",
    tips_tab_help: "Help",
    // In Cuba
    tip_currency_t: "Currency",
    tip_currency_b: "USD and EUR are widely accepted at hotels and many lounges. Cuban Peso (CUP) is needed for street vendors and small shops. Exchange at airport or hotel — avoid street offers.",
    tip_connectivity_t: "Connectivity",
    tip_connectivity_b: "Buy an ETECSA SIM at the airport arrivals hall. Wi-Fi is limited and slow outside hotels. An eSIM activated before arrival keeps you connected immediately.",
    tip_safety_t: "Safety",
    tip_safety_b: "Havana is generally safe for travelers. Stay aware of pickpocketing in Habana Vieja and at tourist hotspots. Keep your phone secure when walking at night.",
    tip_transport_t: "Transport",
    tip_transport_b: "Always negotiate the price before getting into a private taxi. Classic cars are tourist taxis — expect a premium. Habano VIP rides are pre-negotiated and safer.",
    // Arriving
    tip_arr_before_t: "Before you land",
    tip_arr_before_b: "Install a Cuba eSIM before leaving home if your phone supports it (iPhone 14 or newer). Pre-book your first ride to the city. Bring some cash in USD or EUR.",
    tip_arr_airport_t: "At the airport",
    tip_arr_airport_b: "Buy an ETECSA SIM in the arrivals hall. Exchange a small amount of cash for CUP. Bathrooms are past customs.",
    tip_arr_ride_t: "Getting a ride",
    tip_arr_ride_b: "Negotiate price before getting in the car, or use Habano VIP for a pre-set fare. Average taxi to central Havana: 25 to 35 USD.",
    tip_arr_terminal_t: "Terminal info",
    tip_arr_terminal_b: "Terminal 2 handles most international flights. Terminal 3 is the new VIP terminal. ATMs and exchange are after baggage claim.",
    tip_arr_esim_cta: "Get an eSIM for Cuba",
    // Leaving
    tip_lv_timing_t: "Timing",
    tip_lv_timing_b: "Arrive at least 3 hours early for international flights. Security and immigration can be slow.",
    tip_lv_goods_t: "Cuban goods",
    tip_lv_goods_b: "Cigars and rum have per-passenger limits depending on your country. Check your home customs rules before buying in bulk.",
    tip_lv_currency_t: "Currency",
    tip_lv_currency_b: "Spend any remaining CUP before leaving — it cannot be exchanged outside Cuba. Exchange leftover USD or EUR at the airport if needed.",
    tip_lv_airport_t: "Getting to the airport",
    tip_lv_airport_b: "Negotiate price before getting in the car, or pre-book a Habano departure ride. Allow 45 to 60 minutes from central Havana.",
    tip_lv_airport_cta: "Request Airport Ride",
    // Help
    tip_help_emergency_t: "Emergency numbers",
    tip_help_emergency_note: "Cuban emergency services — calls only, no WhatsApp",
    tip_help_police: "Police",
    tip_help_ambulance: "Ambulance",
    tip_help_fire: "Fire",
    tip_help_tourist: "Tourist Police",
    tip_help_support_t: "Habano Support",
    tip_help_support_b: "Reach out to our team for help with rides, bookings, or anything else.",
    tip_help_support_cta: "Contact Habano Support",
    tip_help_lost_t: "Lost or stolen",
    tip_help_lost_b: "Contact your embassy first and report the incident to the Tourist Police. Keep a photocopy of your passport in a separate place. Most hotels can help coordinate.",
    tip_help_embassy_t: "Embassies in Havana",
    tip_help_embassy_b: "United States: Calzada e/L y M, Vedado. Canada: Calle 30 No. 518, Miramar. United Kingdom: Calle 34 No. 702, Miramar. Spain: Cárcel 51, Habana Vieja.",
    journey_guest_unlock: "Sign in to unlock your full Havana journey",
    journey_guest_signin_action: "Sign in →",
    journey_guest_visits_title: "Your Havana visits appear here",
    journey_guest_visits_body: "Sign in to track every place you visit and build your personal journey.",
    journey_guest_vendors_title: "Your trusted vendors appear here",
    journey_guest_vendors_body: "Sign in to save and reconnect with drivers, guides, and local contacts.",
    journey_guest_favorites_title: "Your saved places appear here",
    journey_guest_favorites_body: "Sign in to save your favourite lounges, restaurants, and hidden gems.",
    journey_guest_spend_title: "Your Havana spending appears here",
    journey_guest_spend_body: "Sign in to log and track what you spend on your trip.",
    journey_guest_signin_btn: "Sign In",
    journey_guest_create_btn: "Create Account",
    journey_guest_myplaces_saved_title: "Save unlimited places",
    journey_guest_myplaces_empty_title: "Your saved places appear here",
    journey_guest_myplaces_body: "Sign in to save unlimited custom locations to your journey — restaurants, bars, friends' addresses, anywhere worth remembering.",
    journey_guest_saved_device: "Saved on this device",
    journey_prompt_visits: "track every place you visit",
    journey_prompt_vendors: "save your trusted vendors",
    journey_prompt_favorites: "save your favourite places",
    journey_prompt_spend: "track your Havana spending",
    journey_prompt_myplaces: "save unlimited custom locations",
    journey_prompt_default: "build your Havana journey",
    journey_empty_visits_title: "Your Havana journey starts here",
    journey_empty_visits_body: "Scan a business QR to record your first visit.",
    journey_anonymous: "Anonymous",
    journey_just_for_you_eyebrow: "Just For You",
    journey_empty_vendors_title: "No vendors met yet",
    journey_empty_vendors_body: "Scan a vendor's QR after an interaction — you'll be able to leave them a review.",
    journey_empty_favs_title: "Nothing saved yet",
    journey_empty_favs_body: "Tap the heart on any place or scan a QR to start your collection.",
    journey_total_spent_label: "Total spent",
    journey_across_word: "across",
    journey_private_to_you: "Private to you",
    journey_one_place: "1 place",
    journey_n_places: "{n} places",
    journey_share_trip_btn: "Share My Trip",
    journey_share_trip_msg: "My Havana trip on Habano.app — {places} visited, {total} spent. Discover Havana: habano.app",
    journey_empty_spend_title: "No spending logged yet",
    journey_empty_spend_body: "Open any place or vendor profile and tap Log Spend to track your Havana trip privately.",

    settings_profile_eyebrow: "Profile",
    settings_title: "Settings",
    settings_role_prefix: "role ·",
    settings_offline_title: "Offline Havana",
    settings_offline_sub: "Cuba's connectivity is unreliable. Download once, browse anywhere.",
    settings_offline_status_label: "Status",
    settings_offline_downloaded: "Downloaded",
    settings_offline_not_downloaded: "Not downloaded",
    settings_offline_last_updated: "Last updated",
    settings_offline_storage_used: "Storage used",
    settings_offline_cached: "Cached",
    settings_offline_places_unit: "places",
    settings_offline_favs_unit: "favs",
    settings_offline_visits_unit: "visits",
    settings_offline_map_tiles: "Map tiles",
    settings_offline_pending_token: "Pending Mapbox token",
    settings_offline_active: "Active",
    settings_offline_queued: "Queued",
    settings_offline_tiles_unit: "tiles",
    settings_download_btn: "Download Havana Offline",
    settings_update_btn: "Update offline data",
    settings_remove_btn: "Remove",
    settings_offline_msg: "You're offline. Reconnect to download or update.",
    settings_toast_offline_connect: "You're offline. Connect to download.",
    settings_toast_downloaded: "Havana downloaded for offline use",
    settings_toast_download_failed: "Download failed",
    settings_confirm_remove: "Remove all offline data? Your account stays intact.",
    settings_toast_removed: "Offline data removed",
    settings_toast_vendor_added: "Vendor role added",
    settings_toast_business_added: "Business role added",
    settings_toast_role_failed: "Couldn't add role",
    settings_toast_signed_out: "Signed out",
    settings_link_friends: "Friends",
    settings_link_friends_hint: "See activity and connect",
    settings_link_my_qr: "My QR",
    settings_link_my_qr_hint: "Share to connect with travelers",
    settings_link_notifications: "Notifications",
    settings_link_notifications_hint: "Inbox of activity & alerts",
    settings_link_tips: "Travel tips",
    settings_link_tips_hint: "Airport & Havana essentials",
    settings_link_suggest: "Suggest a place",
    settings_link_suggest_hint: "Add a lounge or vendor we missed",
    settings_link_privacy: "Privacy settings",
    settings_link_privacy_hint: "Sharing with businesses and friends",
    settings_link_language_row: "Language",
    settings_link_language_row_hint: "English · Spanish",
    settings_link_account: "Account",
    settings_link_account_hint: "Email, role, delete account",
    settings_link_connection: "Connection",
    settings_online: "Online",
    settings_offline_word: "Offline",
    settings_add_role_btn: "Add another role",
    settings_add_role_hint: "Register as a Vendor or Business — keep your traveler account.",
    settings_signout: "Log Out",
    settings_signout_confirm_title: "Are you sure you want to log out?",
    settings_signout_confirm_body: "You'll be returned to the splash screen and need to sign in again to access your account.",
    settings_add_role_dialog_title: "Add another role",
    settings_add_role_dialog_sub: "Pick what you want to register as. Your traveler account stays intact.",
    settings_add_role_vendor: "Join as Vendor",
    settings_add_role_business: "Join as Business",
    settings_add_role_current: "Current",

    profile_menu_default_name: "Traveler",
    profile_menu_business_dashboard: "Business Dashboard",
    profile_menu_vendor_dashboard: "Vendor Dashboard",
    profile_menu_admin_dashboard: "Admin Dashboard",
    profile_menu_signout: "Log Out",
    profile_menu_my_tickets: "My Tickets",

    notifications_inbox_eyebrow: "Inbox",
    notifications_title: "Notifications",
    notifications_mark_all_read: "Mark all read",
    notifications_havana_updates_heading: "Havana Updates",
    notifications_all_heading: "All notifications",
    notifications_empty: "Nothing here yet.",

    survival_modal_title: "Havana Survival Guide",
    survival_modal_sub: "Top 10 things to know arriving or departing",
    survival_tab_arriving: "Arriving",
    survival_tab_departing: "Departing",
    survival_close_btn: "Close guide",

    tour_guide_title: "Hire a Tour Guide",
    tour_guide_sub: "Tell us about your tour and we will connect you with a trusted local guide.",
    tour_guide_date: "Date",
    tour_guide_time: "Time",
    tour_guide_people: "People",
    tour_guide_lang_pref: "Guide language preference",
    tour_guide_interests_ph: "cigars, history, nightlife, beaches…",
    tour_guide_name_ph: "Your name",
    tour_guide_special_ph: "Anything we should know?",
    tour_guide_submit: "Open WhatsApp",

    // QuickActions / HotelArrivalBanner / SaveHotelPrompt (within Journey)
    qa_hotel: "Go to my hotel",
    qa_hotel_hint_empty: "Save your hotel first",
    qa_airport: "Airport",
    qa_airport_hint_default: "José Martí Intl",
    qa_emergency: "Emergency",
    qa_emergency_hint: "Police · Ambulance · Tourist police",
    qa_vip: "VIP · Tour guide",
    qa_vip_hint: "Skip the line or book a guide",
    qa_emergency_title: "Emergency contacts",
    qa_emergency_sub: "Havana emergency numbers — tap to call. Works offline.",
    qa_emergency_police: "Police",
    qa_emergency_ambulance: "Ambulance",
    qa_emergency_fire: "Fire",
    qa_emergency_tourist_police: "Tourist Police (MININT)",
    qa_vip_title: "How can we help?",
    qa_vip_sub_choose: "Choose VIP access or book a trusted local guide.",
    qa_vip_sub_vip: "Premium lounges & restaurants with VIP access.",
    qa_vip_sub_guide: "Verified local guides — tap to contact via WhatsApp.",
    qa_vip_access_btn: "VIP Access — skip the line",
    qa_hire_guide_btn: "Hire a Tour Guide",
    qa_no_vip: "No VIP businesses yet.",
    qa_no_guides: "No guides available right now.",
    qa_verified: "VERIFIED",
    qa_active_label: "Active",
    qa_hotel_prompt_title: "Save your hotel first",
    qa_hotel_prompt_sub: "Save your hotel to unlock one-tap directions back any time. Use the \"Save this location\" button on the map or in My Journey.",

    hotel_banner_welcome: "Welcome to Habano — save your hotel so you can always find your way back.",
    hotel_banner_save: "Save My Hotel",
    hotel_banner_later: "Later",
    hotel_arrival_title: "You've arrived — let's save your hotel",
    hotel_arrival_sub: "We'll save your current GPS as your hotel. Tap Save when you're at your hotel and we'll make sure you can always find your way back.",
    hotel_arrival_save_btn: "Save My Hotel Location",
    hotel_arrival_footer: "Not at your hotel yet? Close and tap Save My Hotel from My Journey when you arrive.",
    hotel_toast_saved_authed: "Hotel saved — tap Go to My Hotel anytime",
    hotel_toast_saved_guest: "Hotel saved on this device — sign in to back it up",
    hotel_toast_saved_authed_long: "Hotel saved — tap Go to My Hotel anytime to find your way back",
    hotel_toast_saved_guest_long: "Hotel saved on this device — sign in to back it up to your account",
    hotel_toast_no_location: "Could not get current location",
    hotel_toast_save_failed: "Couldn't save",

    save_hotel_prompt_title: "Save Your Hotel",
    save_hotel_prompt_sub: "Save your hotel location now so you can always find your way back. Tap Save when you are at your hotel.",
    save_hotel_prompt_save_btn: "Save My Hotel Location",
    save_hotel_prompt_later_btn: "Not at my hotel yet",

    // Friends + MyQr
    friends_title: "Friends",
    friends_your_circle: "Your circle",
    friends_my_qr_btn: "My QR",
    friends_tab_activity: "Activity",
    friends_tab_friends: "Friends",
    friends_tab_pending: "Pending",
    friends_activity_empty_title: "No friend activity yet",
    friends_activity_empty_body: "When friends check in, their recent visits show up here. Share check-ins must be on in their privacy settings.",
    friends_activity_at: "at",
    friends_activity_directions: "Directions",
    friends_activity_disclaimer: "Contact your friend before heading there — they may have already left.",
    friends_empty_state: "No friends yet",
    friends_empty_description: "Your friends will appear here",
    friends_empty_body: "Scan a friend's QR or share yours.",
    friends_pending_empty_title: "No pending requests",
    friends_pending_empty_body: "When someone scans your QR, you'll see their request here.",
    friends_accept: "Accept",
    friends_decline: "Decline",
    friends_pending: "Pending",
    friends_add_button: "Add a Friend",
    friends_search_placeholder: "Search friends",
    friends_remove: "Remove Friend",
    friends_unfriend: "Unfriend",
    friends_unfriend_confirm: "Unfriend {name}? Per-user overrides will be removed.",
    friends_unfriend_confirm_generic: "Unfriend this user? Per-user overrides will be removed.",
    friends_request_sent: "Friend request sent",
    friends_request_accepted: "Friend request accepted",
    friends_request_declined: "Friend request declined",
    friends_toast_connected: "Connected",
    friends_toast_declined: "Declined",
    friends_toast_unfriended: "Unfriended",
    friends_share_title: "What do you share with {name}?",
    friends_share_title_generic: "What do you share with this friend?",
    friends_share_description: "These settings override your global defaults for this person only",
    friends_save_preferences: "Save preferences for {name}",
    friends_using_global: "Using global setting",
    friends_custom_override: "Custom override",
    friends_field_name: "Display Name",
    friends_field_photo: "Profile Photo",
    friends_field_email: "Email",
    friends_field_language: "Language",
    friends_field_instagram: "Instagram",
    friends_field_whatsapp: "WhatsApp",
    friends_field_name_long: "Display name",
    friends_field_photo_long: "Profile photo",
    friends_field_email_long: "Email address",
    friends_field_language_long: "Language preference",
    friends_field_instagram_long: "Instagram handle",
    friends_field_whatsapp_long: "WhatsApp",
    friends_state_on: "ON",
    friends_state_off: "OFF",
    friends_state_override_prefix: "Override · global is",
    friends_state_global_prefix: "Global ·",
    friends_reset: "Reset",
    friends_done: "Done",
    friends_close: "Close",

    qr_personal_eyebrow: "Your personal QR",
    qr_personal_title_signin: "Sign in to connect",
    qr_personal_body: "Create a free account to get your personal QR code and connect with friends.",
    qr_personal_cta: "Sign In · Create Account",
    qr_share_eyebrow: "Your QR",
    qr_share_title: "Share your QR code",
    qr_share_scan_label: "Scan to connect",
    qr_share_body: "A friend scans this with the Habano scanner — you'll get a request notification.",

    // Auth + Guest labels
    user_guest: "Guest",
    user_guest_prompt: "Sign in to unlock all features",
    role_user: "Traveler",
    role_vendor: "Vendor",
    role_business: "Business",
    role_admin: "Admin",
    user_role_traveler: "Traveler",
    user_role_business: "Business Owner",
    user_role_vendor: "Vendor",
    auth_log_in: "Create Account / Log In",
    auth_log_out: "Log Out",
    auth_log_out_confirm: "Are you sure you want to log out?",
    auth_log_out_confirm_body: "You'll be returned to the home screen and need to sign in again to access your account.",
    auth_guest_tagline: "Save places, track your journey, connect with friends",
    auth_sign_in: "Sign In",
    auth_sign_in_google: "Sign in with Google",
    auth_create_account: "Create Account",
    auth_welcome_back: "Welcome back",
    auth_continue_guest: "Continue as Guest",
    auth_keep_browsing_guest: "Keep browsing as guest",
    auth_already_have_account: "Already have an account?",
    auth_no_account: "Don't have an account?",
    auth_or_continue_with: "Or continue with",
    auth_agree_terms: "By continuing you agree to our Terms and Privacy Policy",
    auth_join_title: "Join your private Havana",
    auth_join_body: "Create a free account to {action}check in, save places, and track your Havana journey.",
    auth_join_body_no_action: "Create a free account to check in, save places, and track your Havana journey.",
    join_open_app: "Open in Habano App",
    join_add_to_home: "Add to Home Screen",
    join_discover_tagline: "Discover Havana's cigar lifestyle on Habano.app",
    join_create_footer: "Create a free account to check in, save places, and track your Havana journey.",
    select_role_welcome: "Welcome to Habano",
    select_role_pick_title: "Pick how you'll use",
    select_role_pick_sub: "Choose the option that fits. You can add another role later from Settings.",
    select_role_traveler_title: "Traveler / Enthusiast",
    select_role_traveler_body: "Discover Havana's cigar lounges, vendors, and hidden spots. Track your trip privately.",
    select_role_pro_title: "I am a Vendor or Business",
    select_role_pro_body: "List your services, accept check-ins, earn reviews, and grow.",
    select_role_join_vendor: "Join as Vendor",
    select_role_join_business: "Join as Business",
    select_role_continue_btn: "Continue",
    select_role_error_pro: "Please select Vendor or Business to continue",
    select_role_save_error: "Could not save role",
    // Airport v2 — clean two-option layout
    airport_tab_arriving: "ARRIVING",
    airport_tab_departing: "DEPARTING",
    // iter179 — Section eyebrows + titles/descriptions for the new
    // stepped layout (Ride first, VIP second).
    airport_arr_step_eyebrow: "Arriving · Step 1",
    airport_arr_step_desc: "Get a ride from the airport to anywhere in Havana.",
    airport_arr_premium_eyebrow: "Arriving · Premium",
    airport_arr_vip_title: "VIP Arrival",
    airport_arr_vip_desc: "Skip the line. Private transfer, meet and greet service.",
    airport_dep_step_eyebrow: "Departing · Step 1",
    airport_dep_step_desc: "Get a ride to the airport from anywhere in Havana.",
    airport_dep_premium_eyebrow: "Departing · Premium",
    airport_dep_vip_title: "VIP Departure",
    airport_dep_vip_desc: "Private transfer to the airport. On time, every time.",
    airport_arr_vip_t: "VIP Arrival Service",
    airport_arr_vip_s: "Everything handled — no stress, no negotiation",
    airport_arr_vip_pill_1: "Private Transfer",
    airport_arr_vip_pill_2: "Meet & Greet",
    airport_arr_vip_pill_3: "Luggage Assistance",
    airport_arr_vip_pill_4: "Skip the Line",
    airport_arr_vip_cta: "Get VIP Service",
    airport_arr_vip_wa: "Hi, I am arriving at José Martí Airport and I would like VIP arrival service — private transfer, meet and greet, luggage assistance, and skip the line",
    airport_arr_ride_t: "Request Arrival Ride",
    airport_arr_ride_s: "Get a ride from the airport to your destination",
    airport_arr_ride_warn: "Negotiate price before getting in",
    airport_arr_ride_cta: "Request Arrival Ride",
    airport_arr_ride_wa: "Hi, I just arrived at José Martí Airport and I need a ride to my destination through Habano.app",
    airport_dep_vip_t: "VIP Lounge Access",
    airport_dep_vip_s: "Relax and smoke before your flight",
    airport_dep_vip_pill_1: "Lounge Access",
    airport_dep_vip_pill_2: "Cigar Lounge",
    airport_dep_vip_cta: "Get VIP Lounge Access",
    airport_dep_vip_wa: "Hi, I am departing from José Martí Airport and I would like access to the VIP lounge and cigar lounge",
    airport_dep_ride_t: "Request Departure Ride",
    airport_dep_ride_s: "Get a ride to the airport from your location",
    airport_dep_ride_wa: "Hi, I need a ride to José Martí Airport for my departure through Habano.app",
    // Journey extras
    j72_vendors_btn: "My Vendors",
    j72_vendors_sub_n: "{n} saved · drivers, guides & more",
    j72_save_loc_btn: "Save a Location",
    j72_save_loc_sub: "Add a hidden gem to your map",
    // Custom location ride
    cl_request_ride: "Request Ride Here",
    cl_request_ride_wa: "Hi, I need a ride to {name}, {address}",
    // Install page
    install_ios_title: "Add to Home Screen",
    install_ios_sub: "Follow these steps to install Habano on your iPhone",
    install_ios_step_1: "Tap the Share button at the bottom of Safari — if you don't see it, tap the three dots (•••) at the bottom right of your screen first",
    install_ios_step_2: "Scroll down and tap Add to Home Screen",
    install_ios_step_3: "Tap Add in the top right corner",
    install_ios_got_it: "Got it",
    install_android_title: "Install Habano",
    install_android_sub: "Add Habano to your home screen for instant access",
    install_android_cta: "Add to Home Screen",
    install_already: "Habano is already installed ✓",
    install_unavailable_t: "Add Habano to your Home Screen",
    install_unavailable_s: "Open this page in Safari (iPhone) or Chrome (Android) to install Habano as an app.",
    // Iter 74 — MY VENDORS inline section
    j74_vendors_eyebrow: "MY VENDORS",
    j74_vendors_see_all: "See all",
    j74_vendor_type_driver: "Driver",
    j74_vendor_type_tour_guide: "Tour Guide",
    j74_vendor_type_cigar_roller: "Cigar Roller",
    j74_vendor_type_artist: "Artist",
    j74_vendor_type_musician: "Musician",
    j74_vendor_type_photographer: "Photographer",
    j74_vendor_type_chef: "Chef",
    j74_vendor_type_default: "Vendor",
    j74_vendor_whatsapp: "WhatsApp",
    j74_vendor_empty_title: "My Vendors",
    j74_vendor_empty_sub: "Find drivers, guides and more",
    j74_vendor_wa_msg: "Hi {name}, I found you on Habano.app",
    // Iter 76 — Staying-at ride
    staying_ride_wa: "Hi, I need a ride to {name}, {address}",
    ride_unavailable: "Ride service is not available right now",
    // Iter 76 — /tonight page + Saved tabs
    tonight_eyebrow: "TONIGHT",
    tonight_title: "Tonight in Havana",
    tonight_empty: "No spots are open tonight.",
    saved_tab_all: "All",
    saved_tab_businesses: "Businesses",
    saved_tab_vendors: "Vendors",
    saved_tab_gems: "Gems",
    saved_tab_vip: "VIP",
    saved_vip_removed_undo: "Removed",
    undo: "Undo",
    loading: "Loading…",
    menu_my_favorites: "My Favorites",
    menu_my_places: "My Saved Places",
    menu_my_hotel: "My Hotel",
    menu_install_app: "Install App",
    // iter129 — Get Listed + view selector + applications
    menu_get_listed: "List Your Business",
    menu_business_dashboard: "Business Dashboard",
    menu_vendor_dashboard: "Vendor Dashboard",
    menu_application_pending: "Application Pending",
    menu_save_for_offline: "Save for Offline",
    menu_save_for_offline_saving: "Saving…",
    menu_save_for_offline_done: "Offline Ready",
    badge_active: "ACTIVE",
    badge_pending: "PENDING",
    app_pending_review: "Your application is under review — we will notify you within 48 hours",
    your_businesses: "Your Businesses",
    view_selector_title: "Choose Your View",
    view_selector_sub: "You can change this anytime",
    view_tourist_title: "Tourist",
    view_tourist_desc: "For someone visiting Havana who wants to discover the city — restaurants, hotels, airport rides, travel tips and the best of La Habana.",
    view_local_title: "Cubano",
    view_local_desc: "For Havana residents who want quick access to their favorite spots, custom locations and everyday places they return to often.",
    view_save_tourist: "Save — Tourist View",
    view_save_local: "Save — Cubano View",
    // iter147 — Offline & Install page.
    offline_page_title: "Offline & Install",
    offline_page_sub: "Choose how you want to access Habano without internet",
    offline_card_save_title: "Save Havana for Offline",
    offline_card_save_desc: "Download your saved places, map, hotel and businesses so Habano works without internet in Cuba.",
    offline_card_install_title: "Install on Your Device",
    offline_card_install_desc: "Add Habano to your home screen for instant access — no app store needed. Works on iPhone and Android.",
    offline_cta_select: "Select an option",
    offline_cta_save: "Save Havana for Offline",
    offline_cta_install: "Add to Home Screen",
    offline_state_saved: "Saved",
    offline_state_installed: "Installed",
    offline_state_update: "Update",
    offline_row_link: "Offline & Install",
    get_listed_title: "Grow with Habano",
    get_listed_sub: "Join the businesses and vendors shaping the Havana experience",
    get_listed_business_title: "Business",
    get_listed_business_sub: "For businesses with a physical location in Havana",
    get_listed_business_sheet_desc: "For businesses with a physical location in Havana — restaurants, hotels, cigar lounges, rooftop bars and more.",
    get_listed_business_cta: "Apply as Business",
    get_listed_business_tags: "Restaurant · Cigar Lounge · Hotel · Rooftop Bar · Café · Museum",
    get_listed_vendor_title: "Vendor",
    get_listed_vendor_sub: "For service providers — no fixed location needed",
    get_listed_vendor_sheet_desc: "For service providers with no fixed location — drivers, guides, musicians, photographers and more.",
    get_listed_vendor_cta: "Apply as Vendor",
    get_listed_vendor_tags: "Driver · Tour Guide · Musician · Artist · Photographer · Cigar Roller",
    get_listed_sheet_sub: "Choose how you want to join Habano",
    get_listed_select_hint: "Select an option to continue",
    // iter190 — /join marketing page (Grow Your Business).
    join_page_tag: "Join the platform",
    join_page_title_line1: "Grow Your Business With",
    join_page_title_habano: "Habano",
    join_page_sub: "Connect with travelers and locals discovering the best of Havana",
    join_why_title: "Why Habano",
    join_why_1_title: "Get Discovered",
    join_why_1_body: "Reach tourists and locals searching for the best of Havana",
    join_why_2_title: "Ride Requests",
    join_why_2_body: "Customers request rides directly to your location",
    join_why_3_title: "Build Your Reputation",
    join_why_3_body: "Grow with reviews, ratings and VIP access",
    join_business_title: "List My Business",
    join_business_desc: "Physical locations — restaurants, bars, cafes, shops, and more",
    join_business_tags: "Restaurant · Bar · Café · Cigar · + more",
    join_business_cta: "List My Business",
    join_vendor_title: "Become a Vendor",
    join_vendor_desc: "Service providers — drivers, photographers, guides, artists",
    join_vendor_tags: "Driver · Photographer · Guide · Artist",
    join_vendor_cta: "Become a Vendor",
    join_footer_hint: "Select an option above",
    join_lang_english: "English",
    join_lang_spanish: "Español",
    join_back_aria: "Back to home",
    join_choose_label: "Choose how you join",
    join_add_another_business: "Add Another Business",
    join_see_my_business: "See My Business",
    apply_whatsapp_invalid: "WhatsApp number is not valid. Include country code (e.g. +5355559999).",
    // iter195 — Manual ID crop flow.
    crop_step_rotate: "Rotate",
    crop_step_id_eyebrow: "Step 1 of 2",
    crop_step_face_eyebrow: "Step 2 of 2",
    crop_step_preview: "Preview",
    crop_step_submitting: "Uploading…",
    crop_rotate_label: "Rotate if needed",
    crop_rotate_next: "Looks Good",
    crop_step_id_title: "Crop Your ID Card",
    crop_step_id_sub: "Line up your ID card inside the rectangle",
    crop_step_face_title: "Crop Your Face",
    crop_step_face_sub: "Center your face inside the square",
    crop_next: "Next",
    crop_preview_title: "Looks good?",
    crop_preview_id_label: "ID Card",
    crop_preview_face_label: "Your Face",
    crop_preview_redo_id: "Redo ID",
    crop_preview_redo_face: "Redo Face",
    crop_submit: "Submit",
    apply_id_open_crop: "Upload & Crop ID",
    apply_id_change: "Change ID",
    apply_submit: "Submit Application",
    apply_submitted_toast: "Application submitted — we will review within 48 hours",
    // iter181 — Post-submission thank-you screen.
    apply_thanks_title: "Thank You!",
    apply_thanks_sub_business: "Your business application has been submitted. Our team will review it and get back to you soon.",
    apply_thanks_sub_vendor: "Your vendor application has been submitted. Our team will review your ID and get back to you soon.",
    apply_thanks_home_btn: "Back to Home",
    apply_offline_notice: "You are offline — your application will be submitted when you reconnect",
    field_business_name: "Business name",
    field_category: "Category",
    field_address: "Address",
    field_whatsapp: "WhatsApp number",
    field_website: "Website",
    field_instagram: "Instagram",
    field_description: "Description",
    field_full_name: "Full name",
    field_service_type: "Service type",
    field_languages: "Languages spoken",
    field_years_experience: "Years of experience",
    field_brief_description: "Brief description",
    apply_max_300: "Max 300 characters",
    apply_required: "Required",
    // iter177 — Government-ID upload copy for the vendor application.
    apply_gov_id_label: "Government Issued ID",
    apply_gov_id_sub: "Your ID is stored securely and only visible to our team",
    apply_gov_id_button: "Upload ID",
    cat_restaurant: "Restaurant",
    cat_paladar: "Paladar",
    cat_cigar_lounge: "Cigar Lounge",
    cat_hotel: "Hotel",
    cat_rooftop_bar: "Rooftop Bar",
    cat_cafe: "Café",
    cat_bar: "Bar",
    cat_museum: "Museum",
    cat_other: "Other",
    svc_driver: "Driver",
    svc_tour_guide: "Tour Guide",
    svc_musician: "Musician",
    svc_photographer: "Photographer",
    svc_artist: "Artist",
    svc_cigar_roller: "Cigar Roller",
    svc_other: "Other",
    lang_spanish: "Spanish",
    lang_english: "English",
    lang_french: "French",
    lang_german: "German",
    lang_italian: "Italian",
    lang_russian: "Russian",
    lang_other: "Other",
    driver_qr_option_title: "Show Driver QR",
    driver_qr_option_sub: "Show this to your driver to scan",
    driver_qr_title: "Show Driver QR",
    driver_qr_brightness_tip: "Tip: increase your screen brightness for easier scanning outdoors",
    driver_qr_done: "Done",
    map_request_ride: "Transport",
    map_open_in_maps: "Open in Maps",
    map_back_to_home: "Back to Home",
    open_apple_sub: "Native iOS directions",
    open_google_sub: "Open in Google Maps",
    // Iter 81 — Global ride request modal
    ride_modal_title: "Request a Driver",
    ride_modal_sub: "We will send your details to a driver via WhatsApp",
    ride_modal_review_title: "Review your request",
    ride_pickup: "PICKUP",
    ride_destination: "DESTINATION",
    ride_passengers: "PASSENGERS",
    ride_luggage: "LUGGAGE",
    ride_pickup_loading: "Getting your location…",
    ride_pickup_ok: "Location captured ✓",
    ride_pickup_tap: "Tap to enter address manually",
    ride_pickup_manual_hint: "Manual address",
    ride_review_send: "Review & Send",
    ride_edit: "Edit",
    ride_send: "Send",
    ride_qr_offline_note: "The Driver QR works offline — no internet needed",
    ride_request_driver: "Request a Ride",
    ride_request_driver_to_hotel: "Request a Ride to Hotel",
    ride_get_qr: "View Directions",
    ride_qr_works_offline: "Works offline",
    ride_phone_contact: "Call business",
    ride_send_btn: "Request Car Now",
    ride_view_directions: "View Directions",
    ride_share_location: "Share Location",
    ride_open_apple_maps: "Open in Apple Maps",
    ride_open_google_maps: "Open in Google Maps",
    ride_qr_tap_caption: "Tap QR to open in Google Maps",
    contact_btn: "Contact",
    contact_vendor_btn: "Contact Vendor",
    track_spend_title: "Track Spend",
    track_spend_sub: "Log what you spend here",
    log_spend_modal_title: "Log Your Spend",
    log_spend_modal_disclaimer: "This is your personal record — not shared with the business",
    log_spend_amount_placeholder: "How much did you spend here?",
    log_spend_save: "Save",
    log_spend_remove_confirm: "Remove spend tracking for this business?",
    admin_log_spend_label: "Log Spend",
    admin_log_spend_on: "Log Spend enabled — users can track spending at businesses",
    admin_log_spend_off: "Log Spend disabled — spend tracking hidden from all users",
    heart_hint_save: "Save for later",
    // Driver QR enhancements
    driver_qr_tap_open: "Tap QR to open in Google Maps",
    driver_qr_offline_card: "Works offline — no internet needed to show driver",
    driver_qr_save: "Save to Photos",

    // iter101 — Connect page rebuild + dedicated section pages.
    connect_page_title: "Connect",
    connect_scan_title: "Scan any Habano QR",
    connect_scan_sub: "Businesses, vendors or friends",
    connect_scan_cta: "Tap to Start Scanning",
    connect_show_my_qr: "Show My QR",
    connect_more: "MORE",
    connect_tap_to_view: "tap to view →",
    connect_box_friends_label: "FRIENDS",
    connect_box_friends_action: "+ Add Friend",
    connect_box_places_label: "MY PLACES",
    connect_box_places_action: "+ Save Location",
    connect_box_vendors_label: "VENDORS",
    connect_box_vendors_action: "Scan Vendor",
    connect_box_favorites_label: "FAVORITES",
    connect_box_favorites_action: "Discover More",

    // Dedicated section pages.
    page_friends_title: "Friends",
    page_friends_empty: "No friends yet — scan a friend's QR to connect",
    page_friends_pending_banner: "{n} pending request",
    page_friends_pending_banner_plural: "{n} pending requests",
    friends_bar_add: "Add Friend",
    friends_bar_my_qr: "My QR",
    friends_show_qr: "Show QR Code",
    friends_qr_title_my: "My QR Code",
    friends_qr_title_friend: "{name}'s QR",
    friends_qr_caption_my: "Ask your friend to scan this code",
    friends_qr_caption_friend: "Share this with {name} or scan to connect",
    friends_qr_share_link: "Share Link",
    friends_qr_save: "Save QR",
    friends_badge_tourist: "Tourist",
    friends_badge_local: "Cubano",
    friends_scan_camera_title: "Scan a friend's QR",
    friends_scan_add_confirm_title: "Add {name}?",
    friends_scan_add_confirm_cancel: "Cancel",
    friends_scan_add_confirm_confirm: "Add Friend",
    friends_toast_added: "Friend added",
    friends_qr_share_text: "Add me on Habano",

    page_my_places_title: "My Places",
    page_my_places_empty: "No places saved yet — save a location to build your personal Havana map",
    page_my_places_save_cta: "Save a Location",

    page_my_vendors_title: "My Vendors",
    page_my_vendors_empty: "No vendors saved yet — scan a vendor's QR to add them",
    page_my_vendors_chat: "Chat",

    page_my_favorites_title: "Favorites",
    page_my_favorites_empty: "No favorites yet — heart a business to save it here",
  },
  es: {
    app_name: "Habano",
    tagline: "El directorio y compañero para tabaquerías, vendedores y viajeros en La Habana.",
    continue_google: "Continuar con Google",
    splash_eyebrow: "La Habana · Establecido 2025",
    splash_caption: "Hecho para las calles de La Habana — funciona con redes lentas, funciona sin conexión.",
    select_role_title: "Elige tu cuenta",
    select_role_caption: "Puedes cambiarlo luego. Cada camino es distinto.",
    traveler_title: "Viajero · Entusiasta",
    traveler_body: "Encuentra los mejores salones de la ciudad, registra tu visita y arma tu bitácora.",
    vendor_title: "Vendedor",
    vendor_body: "Vendedores verificados. Hazte visible, recibe contactos por WhatsApp, mantente activo.",
    business_title: "Negocio",
    business_body: "Salones, bares, restaurantes — reclama tu perfil, gestiona VIP y aparece en búsquedas.",
    continue: "Continuar",
    welcome: "Bienvenido",
    your_role: "Rol",
    signed_in_as: "Sesión iniciada como",
    logout: "Cerrar Sesión",
    foundation_title: "Estado de la base",
    foundation_caption: "Seis reglas innegociables. Verificadas en vivo contra la base de datos.",
    upload_title: "Pipeline de imágenes",
    upload_caption: "Suelta una imagen. Generamos tres tamaños con carga diferida.",
    drop_here: "Suelta una imagen o haz clic",
    uploading: "Comprimiendo y subiendo…",
    thumbnail: "Miniatura (150px)",
    medium: "Mediana (600px)",
    full: "Completa (1200px)",
    run_test: "Ejecutar",
    home: "Inicio",
    foundation: "Base",
    pipeline: "Pipeline",
    privacy: "Privacidad",
    terms: "Términos",

    // Home screen
    home_editorial: "Editorial",
    home_tonight_in: "Esta noche en",
    home_havana: "La Habana",
    home_see_all: "Ver todo →",
    home_search_placeholder: "Buscar salones, vendedores, ambientes…",
    home_recently_visited_eyebrow: "Continúa tu viaje",
    home_recently_title: "Visitados",
    home_recently_accent: "recientemente",
    home_discover: "Descubrir",
    home_nearby_places: "Lugares cercanos",
    home_all_places: "Todos los lugares",
    home_no_picks: "No hay selecciones destacadas esta noche. Vuelve después del atardecer.",
    home_no_match: "No hay lugares que coincidan con tus filtros.",

    // Category chips
    chip_all: "Todos",
    chip_lounges: "Lounge de Tabacos",
    chip_cigar_shops: "Tabaquería",
    chip_restaurants: "Restaurante",
    chip_rooftops: "Azotea",
    chip_nightlife: "Vida Nocturna",
    chip_hotels: "Hotel",
    chip_beaches: "Playa",
    chip_bar: "Bar",
    chip_local_gem: "Joya Local",

    // iter133 — Shared Filter Places sheet
    filter_places_title: "Filtrar Lugares",
    filter_section_categories: "Categorías",
    filter_open_now: "Abierto Ahora",
    filter_open_now_sub: "Solo lugares actualmente abiertos",
    filter_vip_only: "Solo VIP",
    filter_vip_only_sub: "Solo lugares con VIP activado",
    filter_cigar_friendly: "Permite Tabacos",
    filter_cigar_friendly_sub: "Solo donde se permite fumar",
    filter_distance: "Distancia",
    filter_distance_near: "Cerca (<1 km)",
    filter_distance_walking: "Caminando (1–3 km)",
    filter_any: "Cualquiera",
    filter_min_rating: "Calificación Mínima",
    filter_rating_4plus: "4+ Estrellas",
    filter_rating_3plus: "3+ Estrellas",
    filter_sort_by: "Ordenar Por",
    filter_sort_nearest: "Más Cercano",
    filter_sort_rating: "Mejor Calificado",
    filter_sort_popular: "Más Popular",
    filter_sort_recent: "Más Reciente",
    filter_clear_all: "Limpiar todo",
    filter_show_results: "Ver {n} resultados",
    map_places_nearby: "{n} lugares cerca",
    map_place_nearby_one: "{n} lugar cerca",
    map_search_placeholder: "Buscar lugares…",

    // Exchange rate
    exch_eyebrow: "Datos en vivo · elToque · Mercado Informal",
    exch_title: "Tipos de cambio Cuba",
    exch_official_rates: "Tasas oficiales",
    exch_segmento_iii: "Mercado Informal",
    exch_source_attribution: "Fuente: elToque · Mercado Informal",
    exch_explainer_title: "¿Qué es la tasa del Mercado Informal?",
    exch_explainer_body: "Es la tasa USD/EUR ↔ CUP del mercado informal que los cubanos usan en el día a día. Publicada por elToque a partir de transacciones reales reportadas — suele estar muy por encima de la tasa oficial del Banco Central de Cuba.",
    exch_updated: "Actualizado",
    exch_estimate_only: "Solo estimación — referencia del mercado informal",
    exch_stale_msg: "Tasa no actualizada recientemente — úsala solo como guía.",
    exch_offline_title: "Última tasa disponible",
    exch_history: "Historial de 30 días",

    // Weather
    weather_havana: "Clima en La Habana",
    weather_current_conditions: "Condiciones actuales",
    weather_next_24: "Próximas 24 horas",
    weather_hourly: "Pronóstico por hora",
    weather_7day: "Pronóstico de 7 días",
    weather_morning: "Mañana",
    weather_afternoon: "Tarde",
    weather_evening: "Noche",
    weather_rain_prob: "Prob. lluvia",
    weather_unit: "Unidad de temperatura",
    weather_feels_like: "Sensación térmica",
    weather_humidity: "Humedad",
    weather_wind: "Viento",
    weather_uv: "Índice UV",
    weather_visibility: "Visibilidad",
    weather_sunrise: "Amanecer",
    weather_sunset: "Atardecer",
    weather_now: "Ahora",
    weather_last_updated: "Última actualización",
    weather_stale: "Clima no actualizado recientemente",
    weather_tip_hot: "Día caluroso — hidrátate y planifica visitas en interiores al mediodía.",
    weather_tip_rain: "Se espera lluvia — considera salones interiores y tabaquerías esta noche.",
    weather_tip_uv: "UV alto hoy — usa protección solar si pasas tiempo al aire libre.",
    weather_tip_cool: "Noche fresca — clima perfecto para una terraza con un Habano.",
    weather_tip_perfect: "Noche habanera perfecta — ideal para una terraza o salón al aire libre.",

    // Onboarding
    onb_tagline_1: "La única app que necesitas",
    onb_tagline_2: "para",
    onb_tagline_cuba: "Cuba.",
    onb_subtitle: "Hecho para viajeros. Arraigado en la cultura del tabaco.",
    onb_offline: "Funciona incluso sin conexión a internet",
    onb_check_1: "Conoce la tasa de cambio actual",
    onb_check_2: "Consigue transporte en cualquier lugar de Cuba",
    onb_check_3: "Descubre tabaquerías, restaurantes, terrazas y lugares exclusivos",
    onb_check_4: "Accede a servicios VIP y experiencias curadas",
    onb_check_5: "Guarda lugares favoritos y destinos personalizados — incluso sin conexión",
    onb_whatsapp_required: "WhatsApp obligatorio",
    onb_whatsapp_body: "Toda la comunicación con proveedores de transporte y negocios se realiza por WhatsApp. Asegúrate de tenerlo instalado y funcionando.",
    onb_explore: "Explorar",
    onb_explore_sub: "Salones, restaurantes, lugares",
    onb_explore_cta: "Toca para explorar →",
    onb_transport: "Transporte",
    onb_transport_sub: "Aeropuerto, viajes en ciudad",
    onb_transport_cta: "Pide un viaje →",
    onb_vip: "VIP",
    onb_vip_sub: "Experiencias premium",
    onb_vip_cta: "Acceso VIP →",
    onb_sim_title: "Consigue una SIM o eSIM para Cuba",
    onb_sim_sub: "Mantente conectado — opciones SIM y eSIM para viajeros",
    onb_skip: "Omitir — Llévame a la app",
    onb_focus: "Concéntrate en disfrutar tu viaje, no en resolverlo.",
    onb_legal_prefix: "Al continuar aceptas nuestros",
    onb_legal_terms: "Términos",
    onb_legal_and: "y",
    onb_legal_privacy: "Privacidad",
    /* ---- Profile menu + Journey + Settings (iter 50 i18n retrofit) ---- */
    menu_my_profile: "Mi Perfil",
    menu_my_saved: "Mis Guardados",
    menu_just_for_you: "Para Ti",
    menu_my_journey: "Mi Viaje",
    menu_notifications: "Notificaciones",
    menu_friends: "Amigos",
    menu_my_qr: "Mi Código QR",
    menu_settings: "Configuración",
    menu_privacy_settings: "Privacidad",
    menu_open_support: "Abrir un ticket de soporte",
    menu_business_dashboard: "Panel del Negocio",
    menu_vendor_dashboard: "Panel del Vendedor",
    menu_admin_dashboard: "Panel del Administrador",
    menu_sign_out: "Cerrar Sesión",
    menu_my_tickets: "Mis Tickets",
    menu_my_dashboard: "Mi Panel",
    // Location permission priming
    loc_map_headline: "Descubre La Habana a tu alrededor",
    loc_map_body: "Permite el acceso a tu ubicación para ver lounges de habanos, restaurantes y lugares cercanos con distancias y tiempos de caminata.",
    loc_map_secondary: "Ahora no — explorar sin ubicación",
    loc_ride_headline: "Comparte tu ubicación para el recogido",
    loc_ride_body: "Permite el acceso a tu ubicación para que tu conductor sepa exactamente dónde recogerte en La Habana.",
    loc_ride_secondary: "Ahora no — ingresar dirección manualmente",
    loc_airport_headline: "Encuéntranos en el aeropuerto",
    loc_airport_body: "Permite tu ubicación para confirmar que estás en el Aeropuerto Internacional José Martí y enviar el conductor correcto a tu terminal.",
    loc_airport_secondary: "Ahora no — describiré mi ubicación",
    loc_navigate_headline: "Obtén indicaciones desde donde estás",
    loc_navigate_body: "Permite el acceso a tu ubicación para obtener indicaciones a pie desde tu posición actual hasta este lugar.",
    loc_navigate_secondary: "Ahora no — abrir mapas sin mi ubicación",
    loc_distance_headline: "Ve qué tan lejos estás",
    loc_distance_body: "Permite el acceso a tu ubicación para ver distancias y tiempos de caminata a cada lugar en La Habana.",
    loc_distance_secondary: "Ahora no",
    loc_cta_enable: "Activar Ubicación",
    loc_denied_toast: "Se denegó el acceso a la ubicación — puedes activarlo en los Ajustes de tu teléfono",
    loc_denied_ios_hint: "En iPhone: Ajustes → Safari → Ubicación → Permitir",
    loc_denied_android_hint: "En Android: Ajustes → Configuración del sitio → Ubicación → Permitir",
    loc_denied_revisit: "Para activar la ubicación ve a los Ajustes de tu navegador y permite la ubicación para habano.app",
    settings_reset_location_label: "Restablecer Preferencias de Ubicación",
    settings_reset_location_hint: "Esto permitirá que Habano vuelva a pedirte permiso para acceder a tu ubicación",
    settings_reset_location_confirm_title: "¿Restablecer preferencias de ubicación?",
    settings_reset_location_confirm_body: "Esto restablecerá tus preferencias de ubicación. Se te pedirá nuevamente activar el acceso a la ubicación la próxima vez que uses el mapa o pidas un viaje.",
    settings_reset_location_confirm_cta: "Restablecer preferencias",
    settings_reset_location_cancel: "Cancelar",
    settings_reset_location_toast: "Preferencias de ubicación restablecidas — se te pedirá nuevamente la próxima vez",
    // iter62 — Friend profile
    friend_connected_toast: "Tú y {name} ahora están conectados en Habano",
    friend_profile_back: "Volver a amigos",
    friend_profile_staying_at: "Se hospeda en",
    // iter65 — own profile (ES)
    me_profile_title: "Mi Perfil",
    me_profile_edit: "Editar Perfil",
    me_profile_save: "Guardar",
    me_profile_cancel: "Cancelar",
    me_profile_field_name: "Nombre",
    me_profile_field_photo: "Foto de perfil",
    me_profile_field_instagram: "Instagram",
    me_profile_field_instagram_hint: "El @ se elimina automáticamente",
    me_profile_field_photo_change: "Cambiar foto",
    me_profile_field_photo_upload: "Subir foto",
    me_profile_saved_toast: "Perfil actualizado",
    me_profile_loading: "Cargando perfil…",
    me_profile_share: "Compartir mi perfil",
    me_profile_share_copied: "Enlace de perfil copiado",
    // iter66 — bell dropdown (ES)
    bell_see_all: "Ver todo",
    bell_empty: "Todo al día",
    bell_dismiss: "Descartar",
    friend_profile_favorites_suffix: " — Lugares favoritos",
    friend_profile_saved_suffix: " — Lugares guardados",
    friend_profile_see_all: "Ver todo",
    friend_profile_unfriend: "Eliminar a {name} de amigos",
    friend_profile_unfriend_confirm: "¿Estás seguro de que quieres eliminar a {name} de tus amigos? Se eliminará toda la información compartida entre ustedes.",
    friend_profile_action_whatsapp: "WhatsApp",
    friend_profile_action_ride: "Pedir transporte a su ubicación",
    friend_profile_action_navigate: "Navegar a su ubicación",
    friend_profile_action_instagram: "Ver en Instagram",
    friend_profile_member_since: "Miembro desde",
    friend_profile_badge_explorer: "Explorador",
    friend_profile_badge_enthusiast: "Entusiasta",
    friend_profile_badge_connoisseur: "Conocedor",
    friend_profile_connected: "Ahora están conectados",
    friend_profile_loading: "Cargando perfil del amigo…",
    friend_profile_not_found: "No se pudo cargar el perfil de este amigo.",
    // iter63 — QR scanner page
    scan_card_headline: "Encuentra un QR de Habano",
    scan_card_sub: "Negocio, vendedor, amigo o código de administrador — sabremos cuál.",
    scan_card_cta: "Toca para Escanear",
    scan_show_my_qr: "Mostrar Mi QR",
    scan_more: "MÁS",
    scan_save_a_location: "Guardar una Ubicación",
    scan_my_friends: "Mis Amigos",
    scan_saved_places: "Lugares Guardados",
    scan_your_favorites: "Tus favoritos",
    scan_no_camera_label: "¿SIN CÁMARA? PEGA UN ENLACE",
    nav_scan_label: "ESCANEAR",
    // iter64 — Notifications + signup polish
    notif_group_today: "Hoy",
    notif_group_yesterday: "Ayer",
    notif_group_earlier: "Antes",
    notif_empty_caught_up: "Todo al día",
    notif_detail_close: "Cerrar",
    notif_dismiss_aria: "Descartar",
    traveler_signup_skip: "Saltar este paso",
    traveler_signup_skip_instagram: "Saltar — lo agregaré después",
    journey_eyebrow_passport: "Tu pasaporte",
    journey_havana_updates: "Actualizaciones de La Habana",
    journey_havana_updates_sub: "Últimas alertas e información importante",
    journey_survival_guide: "Guía de Supervivencia de La Habana",
    journey_survival_guide_sub: "Todo lo que necesitas saber",
    journey_traveler_tools: "Herramientas para viajeros",
    journey_travel_tips: "Consejos de viaje",
    journey_get_sim: "Obtén una SIM o eSIM para Cuba",
    journey_continue_trip: "Continúa tu viaje",
    journey_recently_visited: "Visitado recientemente",
    journey_my_places: "Mis Lugares",
    journey_quick_actions: "Acciones rápidas",
    journey_add_location: "Añadir una ubicación",
    journey_no_places: "Aún no hay lugares guardados",
    journey_no_places_sub: "Guarda los negocios que visites para construir tu viaje en La Habana.",
    journey_sign_in_backup: "Inicia sesión para respaldar tus ubicaciones guardadas en tu cuenta",
    journey_total_spent: "Total gastado",
    journey_hire_guide: "Contratar un guía",
    journey_hotel: "Hotel",
    journey_airport: "Aeropuerto",
    journey_emergency: "Emergencia",
    journey_vip: "VIP",
    journey_support: "Soporte",
    onb_headline_part1: "La única app que necesitas para",
    onb_whatsapp_short: "Necesario para transporte y contactos.",
    onb_sim_sub_short: "Mantente conectado. Fácil instalación.",
    onb_sim_esim_badge: "eSIM",
    onb_cta_main: "Entrar a la App",
    onb_footer_secure: "Seguro. Privado. Confiable.",

    // Journey
    journey_title: "Mi viaje",
    journey_quick_actions: "Acciones rápidas",
    journey_traveler_tools: "Herramientas del viajero",
    journey_check_ins: "Registros",
    journey_saved: "Lugares guardados",
    journey_recently_visited: "Visitados recientemente",

    // Airport
    airport_title: "Aeropuerto · Transporte",
    airport_book_ride: "Pedir un viaje",
    airport_arrival: "Recogida de llegada",
    airport_departure: "Salida",
    airport_tips: "Consejos del aeropuerto",

    // Notifications
    notif_welcome: "Bienvenido a Habano",
    notif_urgent: "Actualización urgente",
    notif_customs: "Aviso de aduana",
    notif_currency: "Actualización de moneda",
    notif_event: "Evento esta noche",
    notif_empty: "Aún no hay notificaciones.",

    // Settings — legal block
    settings_legal_eyebrow: "Legal",
    settings_privacy_policy: "Política de privacidad",
    settings_privacy_hint: "Cómo manejamos tus datos",
    settings_terms_service: "Términos de servicio",
    settings_terms_hint: "Reglas para usar Habano",
    settings_terms_accepted_prefix: "Términos y privacidad aceptados el",
    settings_language: "Idioma",
    settings_language_label: "Idioma de la app",
    settings_language_hint: "Los cambios se aplican al instante.",
    map_filter_saved: "Guardados",
    map_filter_open_now: "Abierto",
    map_filter_vip: "VIP",
    map_filter_food: "Comida",
    map_filter_car: "Viaje",
    // iter150 — Category Menu sheet.
    map_filter_menu: "Menú",
    map_menu_clear_all: "Limpiar todo",
    map_menu_title: "Categorías",
    map_menu_search_muted: "Buscar",
    map_menu_search_active: "Buscar · {n} lugares",
    map_menu_search_active_1: "Buscar · 1 lugar",
    map_no_open_food: "No hay restaurantes abiertos cerca",
    map_no_open_now: "Nada abierto cerca ahora mismo",
    map_filter_spots: "Lugares",
    map_recenter: "Centrar en mí",
    map_location_unavailable: "Ubicación no disponible — activa el acceso a la ubicación en los ajustes",
    chip_local_gem: "Joya Local",
    cl_form_new_title: "Guardar una Ubicación",
    // iter67 — minimal save-a-location flow (ES)
    sal_gps_capturing: "Obteniendo tu ubicación...",
    sal_gps_captured: "Ubicación capturada",
    sal_gps_tap: "Toca para obtener la ubicación",
    sal_name_label: "NOMBRE",
    sal_name_placeholder: "¿Cómo se llama este lugar?",
    sal_desc_label: "DESCRIPCIÓN",
    sal_desc_placeholder: "Una nota rápida sobre este lugar",
    sal_whatsapp_label: "WHATSAPP",
    sal_whatsapp_placeholder: "+53 5 555 5555",
    sal_whatsapp_hint: "Número de Cuba",
    sal_save_btn: "Guardar Ubicación",
    sal_only_required: "El nombre es el único campo requerido",
    sal_photo_title: "¿Agregar una Foto?",
    sal_photo_sub: "Ayuda a otros a reconocer este lugar cuando lo compartas",
    sal_photo_btn: "Agregar Foto",
    sal_back_discover: "Volver a Descubrir",
    sal_request_ride: "Pedir un Transporte",
    sal_share_location: "Compartir esta Ubicación",
    sal_upgrade_q: "¿Es este un negocio?",
    sal_upgrade_sub: "Agrega categoría, teléfono, dirección y más",
    sal_share_msg: "Mira este lugar que guardé en La Habana — {link}",
    // iter130 — Interactive crop + Back to Home
    sal_back_home: "Volver al Inicio",
    sal_photo_crop_hint: "Arrastra y pellizca para ajustar",
    sal_photo_save_btn: "Listo",
    // iter68 — Journey dashboard rebuild (ES)
    jd_eyebrow: "MI VIAJE",
    jd_title_a: "Tu",
    jd_title_b: "Habana",
    jd_stat_saved: "GUARDADOS",
    jd_stat_friends: "AMIGOS",
    jd_stat_spots: "MIS LUGARES",
    jd_staying_at: "SE HOSPEDA EN",
    jd_save_hotel_t: "Guardar Mi Hotel o Airbnb",
    jd_save_hotel_s: "Obtén distancias y transporte fácilmente",
    jd_offers_t: "OFERTAS PARA TI",
    jd_my_vendors: "MIS VENDEDORES",
    jd_vip_reminders: "RECORDATORIOS VIP",
    jd_see_all: "Ver todo",
    jd_share_app: "Comparte Habano con un amigo",
    jd_download_app: "Descargar Habano",
    jd_travel_tips: "Consejos de Viaje",
    jd_hotel_fallback: "Mi Hotel",
    jd_share_msg: "Mira Habano — tu concierge para La Habana. https://habano.app",
    jd_vip_removed: "Eliminado de recordatorios VIP",
    jd_undo: "Deshacer",
    // iter69 — Place profile copy (ES)
    pp_contact_wa: "Contactar por WhatsApp",
    pp_contact_call: "Contactar",
    pp_action_ride: "Pedir Transporte",
    pp_action_maps: "Abrir en Mapa",
    pp_action_share: "Compartir",
    pp_action_vip: "Recordatorio VIP",
    pp_action_spend: "Registrar Gasto",
    pp_vip_added: "Añadido a Recordatorios VIP",
    pp_vip_removed: "Eliminado de Recordatorios VIP",
    pp_label_address: "DIRECCIÓN",
    pp_label_phone: "TELÉFONO",
    pp_action_copy: "Copiar",
    pp_action_call: "Llamar",
    pp_action_chat: "Chat",
    pp_action_view: "Ver",
    pp_action_open: "Abrir",
    pp_copied: "Copiado al portapapeles",
    pp_hours_open: "Abierto Ahora",
    pp_hours_closed: "Cerrado",
    pp_hours_all: "Todos los horarios",
    pp_hours_closed_short: "Cerrado",
    pp_nearby_prefix: "Cerca ·",
    pp_nearby_see_all: "Ver todo",
    pp_hours_unavailable: "Horario no disponible",
    // iter71 — Journey rebuild (ES)
    j71_qa_airport_t: "Aeropuerto",
    j71_qa_airport_s: "Traslados y VIP",
    j71_qa_emergency_t: "Emergencia",
    j71_qa_emergency_s: "Números y ayuda",
    j71_qa_tips_t: "Consejos",
    j71_qa_tips_s: "Llegando y saliendo",
    j71_vip_btn: "Recordatorios VIP",
    j71_vip_sub: "{n} negocios guardados",
    j71_vendors_btn: "Mis Vendedores",
    j71_vendors_sub: "{n} guardados · conductores, guías y más",
    j71_tonight_picks: "SELECCIÓN DE ESTA NOCHE",
    j71_recent_favs: "FAVORITOS RECIENTES",
    j71_saved_badge: "Guardado",
    j71_add_home_t: "Añadir a la Pantalla de Inicio",
    j71_add_home_s: "Accede sin un navegador",
    j71_offline_t: "Descargar sin Conexión",
    j71_offline_s: "Funciona sin internet en Cuba",
    j71_offline_t_v2: "Guardar app sin conexión",
    j71_offline_s_v2: "Guardar app para usar sin internet en Cuba",
    j71_offline_loading: "Guardando La Habana…",
    j71_offline_done_label: "Guardado sin conexión",
    j71_offline_done_toast: "App guardada — funciona sin internet ahora",
    save_spot_cta: "Guardar Esta Ubicación",
    save_spot_cta_finding: "Buscando tu ubicación…",
    save_spot_err_perm: "Activa tu ubicación para usar esta función",
    save_spot_err_timeout: "No se pudo obtener tu ubicación — intenta de nuevo",
    journey_app_settings: "CONFIGURACIÓN",
    j_card_save_phone_t: "Guardar en el Teléfono",
    j_card_save_phone_s: "Instala en tu pantalla de inicio",
    j_card_save_offline_t: "Guardar sin Internet",
    j_card_save_offline_s: "Funciona sin internet en Cuba",
    j_card_save_offline_saving_t: "Guardando…",
    j_card_save_offline_saving_s: "Descargando mapa de La Habana",
    j_card_save_offline_done_t: "Listo sin Internet",
    j_card_save_offline_saved_today: "Guardado hoy",
    j_card_save_offline_saved_on: "Guardado {date}",
    j_card_save_offline_update: "Actualizar",
    j_share_btn: "Compartir Habano con un Amigo",
    j_clear_cache_title: "¿Borrar caché sin conexión?",
    j_clear_cache_body: "Esto elimina la app guardada de tu dispositivo. Puedes volver a descargarla cuando tengas buena conexión.",
    j_clear_cache_confirm: "Borrar Caché",
    j_clear_cache_cancel: "Conservar",
    j71_offline_progress: "Descargando La Habana...",
    j71_offline_done: "La Habana está lista sin conexión ✓",
    j71_offline_uptodate: "La Habana está actualizada ✓",
    j71_offline_label: "Descargando La Habana...",
    j71_offline_step_biz: "Guardando negocios",
    j71_offline_step_photos: "Guardando fotos",
    j71_offline_step_fx: "Guardando tasa de cambio",
    j71_offline_step_tips: "Guardando consejos",
    j71_offline_done_btn: "Hecho",
    j71_empty_t: "Empieza a explorar La Habana",
    j71_empty_s: "Guarda negocios y lugares para crear tu guía personal de La Habana",
    j71_empty_cta: "Descubrir Lugares",
    j71_share_t: "Compartir Habano",
    j71_share_s: "Comparte con un amigo",
    cl_form_edit_title: "Editar Ubicación",
    cl_form_local_gem_note: "Todas las ubicaciones personalizadas comienzan como Joya Local.",
    cl_form_photos: "Fotos",
    cl_form_add_photo: "Añadir foto",
    cl_form_basics: "Básico",
    cl_form_location: "Ubicación",
    cl_form_contact: "Contacto",
    cl_form_visiting: "Visita",
    cl_form_my_notes: "Mis Notas",
    cl_form_my_notes_help: "Notas privadas — visibles sólo para ti.",
    cl_form_name: "Nombre",
    cl_form_description: "Descripción",
    cl_form_address: "Dirección",
    cl_form_phone: "Teléfono",
    cl_form_whatsapp: "WhatsApp",
    cl_form_instagram: "Instagram",
    cl_form_website: "Sitio web",
    cl_form_hours: "Horario",
    cl_form_smoking_policy: "Política de fumadores",
    cl_form_optional: "opcional",
    cl_form_request_directory: "Solicitar añadir esta ubicación al directorio Habano",
    cl_form_request_directory_help: "Nuestro equipo revisará y la añadirá si es aprobada.",
    cl_form_save_btn: "Guardar Ubicación",
    add_location_btn: "Añadir Ubicación",
    tour_guide_interests: "Intereses del tour (opcional)",
    tour_guide_name: "Tu nombre",
    tour_guide_special: "Solicitudes especiales (opcional)",
    tour_guide_pickup: "Lugar de recogida (opcional)",
    tour_guide_pickup_placeholder: "¿Dónde te recogemos?",
    support_button: "Soporte",
    support_headline: "¿En qué podemos ayudarte?",
    support_subtext: "Cuéntanos qué pasó y te responderemos por WhatsApp.",
    support_name: "Nombre",
    support_email: "Correo",
    support_category: "Categoría",
    support_message: "Mensaje",
    support_message_placeholder: "Describe qué pasó",
    support_screenshot: "Captura (opcional)",
    support_screenshot_choose: "Adjuntar captura",
    support_screenshot_change: "Cambiar captura",
    support_screenshot_fail: "No pudimos procesar esa imagen — prueba con una más pequeña.",
    support_submit: "Enviar solicitud",
    support_submitting: "Enviando…",
    support_missing_fields: "Por favor completa nombre, correo y un mensaje (al menos 4 caracteres).",
    support_ticket_label: "Número de ticket",
    support_confirm_with_wa: "Tu solicitud fue enviada. Te responderemos por WhatsApp en breve.",
    support_confirm_no_wa: "Tu solicitud fue enviada. Nuestro equipo te contactará por correo.",
    support_done: "Listo",
    connected_eyebrow: "Mantente conectado",
    connected_title: "Mantente conectado en Cuba",
    connected_subtitle: "Elige la mejor opción para tu teléfono.",
    connected_sim_card: "Mi teléfono tiene ranura SIM",
    connected_esim_only: "Mi teléfono solo soporta eSIM",
    connected_esim_only_sub: "iPhone 14 y modelos US más recientes",
    connected_sim_label: "Tarjeta SIM física",
    connected_sim_headline: "Cómprala en el aeropuerto al llegar",
    connected_sim_step_1: "Busca el puesto de ETECSA junto a la zona de recogida de equipaje — el personal te ayudará a comprar una SIM turística Cubacel y a configurarla.",
    connected_sim_step_2: "Carga datos de inmediato mientras todavía tienes conectividad en el aeropuerto.",
    connected_sim_step_3: "Guarda el empaque de la SIM — puedes necesitar el número para WhatsApp.",
    connected_sim_warning: "Compra tu SIM antes de salir de la zona de llegadas. Una vez fuera del aeropuerto es más difícil encontrarla.",
    connected_sim_cost_label: "Costo",
    connected_sim_cost: "Las SIM turísticas suelen costar unos pocos USD — los precios pueden variar.",
    connected_sim_compat_label: "Compatibilidad",
    connected_sim_compat: "Verifica que tu teléfono esté desbloqueado antes de viajar — un teléfono bloqueado no funcionará con una SIM cubana.",
    connected_esim_label: "Proveedores eSIM para Cuba",
    connected_esim_critical_strong: "Instala tu eSIM ANTES de salir de casa",
    connected_esim_critical_body: "necesitas wifi para instalarla y puede que no tengas conexión al aterrizar.",
    connected_get_esim: "Obtener eSIM →",
    connected_esim_note_label: "Nota",
    connected_esim_note: "Todas las eSIM son solo de datos — sin número telefónico cubano local. Aún puedes usar llamadas y mensajes de WhatsApp por datos.",
    connected_esim_tip_label: "Compatibilidad eSIM",
    connected_esim_tip: "¿No sabes si tu teléfono soporta eSIM? Ve a Configuración → General → Información y busca Bloqueo de operador u opciones eSIM.",
    connected_tips_label: "Bueno saber",
    connected_tip_1: "La conectividad en Cuba está gestionada por ETECSA — el proveedor estatal de telecomunicaciones.",
    connected_tip_2: "Las velocidades de datos pueden ser lentas o intermitentes — descarga Habano sin conexión antes de llegar.",
    connected_tip_3: "El wifi del aeropuerto está disponible en la zona de llegadas — úsalo para instalar tu eSIM y descargar datos offline.",
    connected_tip_4: "La mayoría de los hoteles y Airbnbs tienen wifi — pide la contraseña a tu anfitrión al llegar.",
    connected_download_offline: "Descargar Habano sin conexión",
    connected_view_tips: "Ver consejos de viaje",

    // Generic
    back: "Atrás",
    go_to_app: "Ir a la app",
    cancel: "Cancelar",
    save: "Guardar",
    error_generic: "Algo salió mal. Inténtalo de nuevo.",
    offline_banner: "Estás sin conexión — mostrando datos en caché.",

    // ---- Traveler app i18n retrofit (Journey, Settings, ProfileMenu, Notifications, modals) ----
    loading_ellipsis: "Cargando…",
    removed_placeholder: "(eliminado)",
    nav_discover: "Descubrir",
    nav_map: "Mapa",
    nav_scan: "Escanear",
    nav_qr: "QR",
    nav_journey: "Ruta",
    nav_profile: "Perfil",
    nav_car: "Car",
    nav_ride: "Viaje",

    // ---- Transportation Hub (/car) ----
    car_eyebrow: "TRANSPORTE",
    car_where: "¿A",
    car_to: "dónde?",
    car_search_ph: "Busca entre tus lugares guardados, hotel, lugares personalizados…",
    car_map_search_ph: "Buscar en La Habana...",
    car_map_search_clear: "Borrar",
    car_map_search_no_results: "Sin resultados",
    car_map_search_hotel: "Mi Hotel",
    car_map_search_section_saved: "Guardados",
    car_map_search_section_places: "Lugares",
    car_map_search_section_geocode: "La Habana",
    car_staying_at: "SE HOSPEDA EN",
    car_saved_places: "LUGARES GUARDADOS",
    car_recent_rides: "VIAJES RECIENTES",
    car_find_on_map: "Buscar en el Mapa",
    car_find_on_map_sub: "Arrastra y fija cualquier lugar en La Habana",
    car_save_hotel_prompt: "Guarda tu hotel para pedir un viaje de regreso",
    car_save_hotel_cta: "Guardar mi hotel",
    car_no_saved_places: "Aún no tienes lugares guardados — guarda negocios o crea lugares personalizados para verlos aquí.",

    // ---- Find on Map (/car/map) ----
    car_map_title: "Buscar en el Mapa",
    car_map_sub: "Arrastra el mapa para elegir tu destino",
    car_map_panel_drag: "Arrastra el mapa para mover el pin",
    car_map_panel_cta: "Pedir un Viaje Aquí",
    // iter153 — Pickup location field on the ride popup.
    ride_pickup_label: "Recogerme en",
    ride_pickup_loading: "Detectando tu ubicación...",
    ride_pickup_unavailable: "Ubicación no disponible",
    ride_pickup_change: "Cambiar →",
    ride_pickup_set_here: "Establecer Recogida Aquí",
    car_map_panel_hint: "Abre la solicitud de viaje con este lugar",
    car_map_change: "Cambiar",
    car_map_loading: "Buscando ubicación…",
    car_map_offline_coords: "Sin conexión — usando coordenadas",
    car_map_no_token: "Mapa no disponible — falta el token de Mapbox.",
    car_map_search_results: "Toca un lugar abajo para colocar el pin",
    car_map_from_airport_title: "¿Adónde en La Habana?",
    car_map_from_airport_sub: "Fija un destino — la recogida está fijada al Aeropuerto José Martí.",

    journey_tab_visits: "Visitas",
    journey_tab_my_places: "Mis Lugares",
    journey_tab_vendors: "Vendedores",
    journey_tab_favorites: "Favoritos",
    journey_tab_spend: "Gastos",
    journey_heading_my_havana: "Mi Habana",
    journey_heading_journey: "viaje",
    journey_subtitle: "Cada check-in, cada vendedor, cada favorito — todo unido. Solo tuyo, a menos que decidas compartirlo.",
    journey_stamp_visits: "Visitas",
    journey_stamp_vendors: "Vendedores",
    journey_stamp_favs: "Favs",
    journey_save_hotel_cta: "Toca aquí para guardar tu hotel al llegar",
    journey_save_badge: "Guardar",
    journey_survival_sub_short: "Las 10 cosas que debes saber al llegar o salir",
    journey_tool_airport: "Aeropuerto",
    journey_tool_tips: "Consejos de viaje",
    journey_sim_sub_alt: "Mantente conectado — opciones SIM y eSIM para viajeros",
    // iter65 — Journey hub redesign (ES)
    hub_staying_at: "ALOJAMIENTO",
    hub_just_for_you: "PARA TI",
    hub_airport_label: "Aeropuerto",
    hub_airport_desc: "Servicio VIP, traslados y ayuda de llegada",
    hub_sim_label: "Obtén una SIM o eSIM",
    hub_sim_desc: "Mantente conectado en Cuba",
    hub_saved_label: "Mis Lugares Guardados",
    hub_saved_desc: "Tus negocios y ubicaciones favoritos",
    hub_tips_label: "Consejos de Viaje",
    hub_tips_desc: "Llegando, en Cuba, saliendo y ayuda",
    // Airport page rebuild (ES)
    airport_eyebrow: "AEROPUERTO",
    airport_title: "Aeropuerto Internacional José Martí",
    airport_subtitle: "Terminal 2 · Vuelos Internacionales",
    airport_vip_label: "Servicio VIP en el Aeropuerto",
    airport_vip_desc: "Traslado privado, bienvenida personalizada, asistencia con equipaje — sin negociación",
    airport_vip_cta: "Solicitar Servicio VIP",
    airport_arriving_label: "Llegando — Transporte desde el Aeropuerto",
    airport_arriving_desc: "Solicita un transporte desde el Aeropuerto José Martí a tu destino",
    airport_departing_label: "Saliendo — Transporte al Aeropuerto",
    airport_departing_desc: "Solicita un transporte al Aeropuerto José Martí desde tu ubicación",
    airport_negotiate_warn: "Negocia el precio antes de subir al auto",
    airport_negotiate_note: "El precio se negocia directamente con tu conductor por WhatsApp",
    airport_arriving_cta: "Solicitar Transporte de Llegada",
    airport_departing_cta: "Solicitar Transporte de Salida",
    // Tips page rebuild (ES)
    tips_eyebrow: "CONSEJOS DE VIAJE",
    tips_title: "Guía de Viaje a La Habana",
    tips_tab_in_cuba: "En Cuba",
    tips_tab_arriving: "Llegando",
    tips_tab_leaving: "Saliendo",
    tips_tab_help: "Ayuda",
    // In Cuba (ES)
    tip_currency_t: "Moneda",
    tip_currency_b: "USD y EUR se aceptan en hoteles y muchos salones. Necesitas Peso Cubano (CUP) para vendedores callejeros y tiendas pequeñas. Cambia dinero en el aeropuerto o tu hotel — evita ofertas de la calle.",
    tip_connectivity_t: "Conectividad",
    tip_connectivity_b: "Compra una SIM de ETECSA en el aeropuerto al llegar. El Wi-Fi es limitado y lento fuera de los hoteles. Una eSIM activada antes te mantiene conectado al instante.",
    tip_safety_t: "Seguridad",
    tip_safety_b: "La Habana es generalmente segura para viajeros. Cuidado con carteristas en Habana Vieja y zonas turísticas. Guarda tu teléfono al caminar de noche.",
    tip_transport_t: "Transporte",
    tip_transport_b: "Siempre negocia el precio antes de subir a un taxi privado. Los autos clásicos son taxis turísticos — espera un sobreprecio. Los traslados VIP de Habano son pre-negociados y más seguros.",
    // Arriving (ES)
    tip_arr_before_t: "Antes de aterrizar",
    tip_arr_before_b: "Instala una eSIM para Cuba antes de salir si tu teléfono lo permite (iPhone 14 o más nuevo). Reserva tu primer transporte. Lleva algo de efectivo en USD o EUR.",
    tip_arr_airport_t: "En el aeropuerto",
    tip_arr_airport_b: "Compra una SIM de ETECSA en la zona de llegadas. Cambia algo de efectivo a CUP. Los baños están pasando aduana.",
    tip_arr_ride_t: "Conseguir transporte",
    tip_arr_ride_b: "Negocia el precio antes de subir, o usa Habano VIP para una tarifa fija. Un taxi al centro cuesta entre 25 y 35 USD.",
    tip_arr_terminal_t: "Información de terminal",
    tip_arr_terminal_b: "La Terminal 2 maneja la mayoría de vuelos internacionales. La Terminal 3 es la nueva terminal VIP. Cajeros y cambio de moneda están tras el reclamo de equipaje.",
    tip_arr_esim_cta: "Obtén una eSIM para Cuba",
    // Leaving (ES)
    tip_lv_timing_t: "Tiempo",
    tip_lv_timing_b: "Llega al menos 3 horas antes para vuelos internacionales. La seguridad y migración pueden ser lentas.",
    tip_lv_goods_t: "Productos cubanos",
    tip_lv_goods_b: "Los puros y el ron tienen límites por pasajero según tu país. Revisa las reglas de aduana de tu país antes de comprar en cantidad.",
    tip_lv_currency_t: "Moneda",
    tip_lv_currency_b: "Gasta cualquier CUP restante antes de salir — no se puede cambiar fuera de Cuba. Cambia tus USD o EUR sobrantes en el aeropuerto si lo necesitas.",
    tip_lv_airport_t: "Llegar al aeropuerto",
    tip_lv_airport_b: "Negocia el precio antes de subir al auto, o reserva un transporte de salida con Habano. Calcula entre 45 y 60 minutos desde el centro.",
    tip_lv_airport_cta: "Solicitar Transporte al Aeropuerto",
    // Help (ES)
    tip_help_emergency_t: "Números de emergencia",
    tip_help_emergency_note: "Servicios de emergencia cubanos — solo llamadas, sin WhatsApp",
    tip_help_police: "Policía",
    tip_help_ambulance: "Ambulancia",
    tip_help_fire: "Bomberos",
    tip_help_tourist: "Policía Turística",
    tip_help_support_t: "Soporte Habano",
    tip_help_support_b: "Contáctanos para ayuda con transportes, reservas o cualquier otra cosa.",
    tip_help_support_cta: "Contactar Soporte Habano",
    tip_help_lost_t: "Perdido o robado",
    tip_help_lost_b: "Contacta primero a tu embajada y reporta el incidente a la Policía Turística. Guarda una copia de tu pasaporte por separado. La mayoría de hoteles pueden ayudarte a coordinar.",
    tip_help_embassy_t: "Embajadas en La Habana",
    tip_help_embassy_b: "Estados Unidos: Calzada e/L y M, Vedado. Canadá: Calle 30 No. 518, Miramar. Reino Unido: Calle 34 No. 702, Miramar. España: Cárcel 51, Habana Vieja.",
    journey_guest_unlock: "Inicia sesión para desbloquear todo tu viaje en La Habana",
    journey_guest_signin_action: "Iniciar sesión →",
    journey_guest_visits_title: "Tus visitas en La Habana aparecerán aquí",
    journey_guest_visits_body: "Inicia sesión para registrar cada lugar que visites y construir tu viaje personal.",
    journey_guest_vendors_title: "Tus vendedores de confianza aparecerán aquí",
    journey_guest_vendors_body: "Inicia sesión para guardar y reconectar con choferes, guías y contactos locales.",
    journey_guest_favorites_title: "Tus lugares guardados aparecerán aquí",
    journey_guest_favorites_body: "Inicia sesión para guardar tus salones, restaurantes y joyas escondidas favoritas.",
    journey_guest_spend_title: "Tus gastos en La Habana aparecerán aquí",
    journey_guest_spend_body: "Inicia sesión para registrar y seguir lo que gastas en tu viaje.",
    journey_guest_signin_btn: "Iniciar sesión",
    journey_guest_create_btn: "Crear cuenta",
    journey_guest_myplaces_saved_title: "Guarda lugares ilimitados",
    journey_guest_myplaces_empty_title: "Tus lugares guardados aparecerán aquí",
    journey_guest_myplaces_body: "Inicia sesión para guardar ubicaciones personalizadas ilimitadas en tu viaje — restaurantes, bares, direcciones de amigos, cualquier lugar que valga la pena recordar.",
    journey_guest_saved_device: "Guardado en este dispositivo",
    journey_prompt_visits: "registrar cada lugar que visites",
    journey_prompt_vendors: "guardar tus vendedores de confianza",
    journey_prompt_favorites: "guardar tus lugares favoritos",
    journey_prompt_spend: "seguir tus gastos en La Habana",
    journey_prompt_myplaces: "guardar ubicaciones personalizadas ilimitadas",
    journey_prompt_default: "construir tu viaje en La Habana",
    journey_empty_visits_title: "Tu viaje en La Habana comienza aquí",
    journey_empty_visits_body: "Escanea el QR de un negocio para registrar tu primera visita.",
    journey_anonymous: "Anónimo",
    journey_just_for_you_eyebrow: "Para Ti",
    journey_empty_vendors_title: "Aún no has conocido vendedores",
    journey_empty_vendors_body: "Escanea el QR de un vendedor después de una interacción — podrás dejarle una reseña.",
    journey_empty_favs_title: "Aún no tienes nada guardado",
    journey_empty_favs_body: "Toca el corazón en cualquier lugar o escanea un QR para comenzar tu colección.",
    journey_total_spent_label: "Total gastado",
    journey_across_word: "en",
    journey_private_to_you: "Privado para ti",
    journey_one_place: "1 lugar",
    journey_n_places: "{n} lugares",
    journey_share_trip_btn: "Compartir mi viaje",
    journey_share_trip_msg: "Mi viaje a La Habana en Habano.app — {places} visitados, {total} gastado. Descubre La Habana: habano.app",
    journey_empty_spend_title: "Aún no has registrado gastos",
    journey_empty_spend_body: "Abre cualquier lugar o perfil de vendedor y toca Registrar gasto para seguir tu viaje de forma privada.",

    settings_profile_eyebrow: "Perfil",
    settings_title: "Configuración",
    settings_role_prefix: "rol ·",
    settings_offline_title: "La Habana sin conexión",
    settings_offline_sub: "La conectividad en Cuba es poco fiable. Descarga una vez y navega en cualquier lugar.",
    settings_offline_status_label: "Estado",
    settings_offline_downloaded: "Descargado",
    settings_offline_not_downloaded: "No descargado",
    settings_offline_last_updated: "Última actualización",
    settings_offline_storage_used: "Espacio usado",
    settings_offline_cached: "En caché",
    settings_offline_places_unit: "lugares",
    settings_offline_favs_unit: "favs",
    settings_offline_visits_unit: "visitas",
    settings_offline_map_tiles: "Mapas",
    settings_offline_pending_token: "Pendiente del token de Mapbox",
    settings_offline_active: "Activo",
    settings_offline_queued: "En cola",
    settings_offline_tiles_unit: "celdas",
    settings_download_btn: "Descargar La Habana sin conexión",
    settings_update_btn: "Actualizar datos sin conexión",
    settings_remove_btn: "Eliminar",
    settings_offline_msg: "Estás sin conexión. Reconéctate para descargar o actualizar.",
    settings_toast_offline_connect: "Estás sin conexión. Conéctate para descargar.",
    settings_toast_downloaded: "La Habana descargada para uso sin conexión",
    settings_toast_download_failed: "La descarga falló",
    settings_confirm_remove: "¿Eliminar todos los datos sin conexión? Tu cuenta no se verá afectada.",
    settings_toast_removed: "Datos sin conexión eliminados",
    settings_toast_vendor_added: "Rol de vendedor añadido",
    settings_toast_business_added: "Rol de negocio añadido",
    settings_toast_role_failed: "No se pudo añadir el rol",
    settings_toast_signed_out: "Sesión cerrada",
    settings_link_friends: "Amigos",
    settings_link_friends_hint: "Ver actividad y conectar",
    settings_link_my_qr: "Mi QR",
    settings_link_my_qr_hint: "Comparte para conectar con viajeros",
    settings_link_notifications: "Notificaciones",
    settings_link_notifications_hint: "Bandeja de actividad y alertas",
    settings_link_tips: "Consejos de viaje",
    settings_link_tips_hint: "Aeropuerto y esenciales de La Habana",
    settings_link_suggest: "Sugerir un lugar",
    settings_link_suggest_hint: "Añade un salón o vendedor que nos faltó",
    settings_link_privacy: "Configuración de privacidad",
    settings_link_privacy_hint: "Compartir con negocios y amigos",
    settings_link_language_row: "Idioma",
    settings_link_language_row_hint: "Inglés · Español",
    settings_link_account: "Cuenta",
    settings_link_account_hint: "Correo, rol, eliminar cuenta",
    settings_link_connection: "Conexión",
    settings_online: "En línea",
    settings_offline_word: "Sin conexión",
    settings_add_role_btn: "Añadir otro rol",
    settings_add_role_hint: "Regístrate como Vendedor o Negocio — conserva tu cuenta de viajero.",
    settings_signout: "Cerrar Sesión",
    settings_signout_confirm_title: "¿Estás seguro de que quieres cerrar sesión?",
    settings_signout_confirm_body: "Volverás a la pantalla inicial y deberás iniciar sesión de nuevo para acceder a tu cuenta.",
    settings_add_role_dialog_title: "Añadir otro rol",
    settings_add_role_dialog_sub: "Elige cómo quieres registrarte. Tu cuenta de viajero no se verá afectada.",
    settings_add_role_vendor: "Únete como Vendedor",
    settings_add_role_business: "Únete como Negocio",
    settings_add_role_current: "Actual",

    profile_menu_default_name: "Viajero",
    profile_menu_business_dashboard: "Panel del Negocio",
    profile_menu_vendor_dashboard: "Panel del Vendedor",
    profile_menu_admin_dashboard: "Panel del Administrador",
    profile_menu_signout: "Cerrar Sesión",
    profile_menu_my_tickets: "Mis Tickets",

    notifications_inbox_eyebrow: "Bandeja",
    notifications_title: "Notificaciones",
    notifications_mark_all_read: "Marcar todas como leídas",
    notifications_havana_updates_heading: "Actualizaciones de La Habana",
    notifications_all_heading: "Todas las notificaciones",
    notifications_empty: "Aún no hay nada aquí.",

    survival_modal_title: "Guía de supervivencia de La Habana",
    survival_modal_sub: "Las 10 cosas que debes saber al llegar o salir",
    survival_tab_arriving: "Llegada",
    survival_tab_departing: "Salida",
    survival_close_btn: "Cerrar guía",

    tour_guide_title: "Contratar un guía",
    tour_guide_sub: "Cuéntanos sobre tu tour y te conectaremos con un guía local de confianza.",
    tour_guide_date: "Fecha",
    tour_guide_time: "Hora",
    tour_guide_people: "Personas",
    tour_guide_lang_pref: "Idioma del guía",
    tour_guide_interests_ph: "tabacos, historia, vida nocturna, playas…",
    tour_guide_name_ph: "Tu nombre",
    tour_guide_special_ph: "¿Algo más que debamos saber?",
    tour_guide_submit: "Abrir WhatsApp",

    // QuickActions / HotelArrivalBanner / SaveHotelPrompt (within Journey)
    qa_hotel: "Ir a mi hotel",
    qa_hotel_hint_empty: "Guarda tu hotel primero",
    qa_airport: "Aeropuerto",
    qa_airport_hint_default: "José Martí Intl",
    qa_emergency: "Emergencia",
    qa_emergency_hint: "Policía · Ambulancia · Policía turística",
    qa_vip: "VIP · Guía turístico",
    qa_vip_hint: "Sáltate la fila o reserva un guía",
    qa_emergency_title: "Contactos de emergencia",
    qa_emergency_sub: "Números de emergencia de La Habana — toca para llamar. Funciona sin conexión.",
    qa_emergency_police: "Policía",
    qa_emergency_ambulance: "Ambulancia",
    qa_emergency_fire: "Bomberos",
    qa_emergency_tourist_police: "Policía turística (MININT)",
    qa_vip_title: "¿En qué podemos ayudarte?",
    qa_vip_sub_choose: "Elige acceso VIP o reserva un guía local de confianza.",
    qa_vip_sub_vip: "Salones y restaurantes premium con acceso VIP.",
    qa_vip_sub_guide: "Guías locales verificados — toca para contactar por WhatsApp.",
    qa_vip_access_btn: "Acceso VIP — sáltate la fila",
    qa_hire_guide_btn: "Contratar un guía",
    qa_no_vip: "Aún no hay negocios VIP.",
    qa_no_guides: "No hay guías disponibles en este momento.",
    qa_verified: "VERIFICADO",
    qa_active_label: "Activo",
    qa_hotel_prompt_title: "Guarda tu hotel primero",
    qa_hotel_prompt_sub: "Guarda tu hotel para desbloquear direcciones de regreso con un toque. Usa el botón \"Guardar esta ubicación\" en el mapa o en Mi Viaje.",

    hotel_banner_welcome: "Bienvenido a Habano — guarda tu hotel para que siempre puedas encontrar el camino de regreso.",
    hotel_banner_save: "Guardar mi hotel",
    hotel_banner_later: "Más tarde",
    hotel_arrival_title: "Has llegado — guardemos tu hotel",
    hotel_arrival_sub: "Guardaremos tu ubicación GPS actual como tu hotel. Toca Guardar cuando estés en tu hotel y nos aseguraremos de que siempre puedas encontrar el camino de regreso.",
    hotel_arrival_save_btn: "Guardar la ubicación de mi hotel",
    hotel_arrival_footer: "¿Aún no estás en tu hotel? Cierra y toca Guardar mi hotel desde Mi Viaje cuando llegues.",
    hotel_toast_saved_authed: "Hotel guardado — toca Ir a mi hotel cuando quieras",
    hotel_toast_saved_guest: "Hotel guardado en este dispositivo — inicia sesión para respaldarlo",
    hotel_toast_saved_authed_long: "Hotel guardado — toca Ir a mi hotel para encontrar el camino de regreso",
    hotel_toast_saved_guest_long: "Hotel guardado en este dispositivo — inicia sesión para respaldarlo en tu cuenta",
    hotel_toast_no_location: "No se pudo obtener la ubicación actual",
    hotel_toast_save_failed: "No se pudo guardar",

    save_hotel_prompt_title: "Guarda tu hotel",
    save_hotel_prompt_sub: "Guarda la ubicación de tu hotel ahora para que siempre puedas encontrar el camino de regreso. Toca Guardar cuando estés en tu hotel.",
    save_hotel_prompt_save_btn: "Guardar la ubicación de mi hotel",
    save_hotel_prompt_later_btn: "Aún no estoy en mi hotel",

    // Friends + MyQr
    friends_title: "Amigos",
    friends_your_circle: "Tu círculo",
    friends_my_qr_btn: "Mi QR",
    friends_tab_activity: "Actividad",
    friends_tab_friends: "Amigos",
    friends_tab_pending: "Pendientes",
    friends_activity_empty_title: "Aún no hay actividad de amigos",
    friends_activity_empty_body: "Cuando tus amigos hagan check-in, sus visitas recientes aparecerán aquí. Deben tener activado compartir check-ins en su configuración de privacidad.",
    friends_activity_at: "en",
    friends_activity_directions: "Cómo llegar",
    friends_activity_disclaimer: "Contacta a tu amigo antes de ir — puede que ya se haya ido.",
    friends_empty_state: "Aún no tienes amigos",
    friends_empty_description: "Tus amigos aparecerán aquí",
    friends_empty_body: "Escanea el QR de un amigo o comparte el tuyo.",
    friends_pending_empty_title: "No hay solicitudes pendientes",
    friends_pending_empty_body: "Cuando alguien escanee tu QR, verás su solicitud aquí.",
    friends_accept: "Aceptar",
    friends_decline: "Rechazar",
    friends_pending: "Pendiente",
    friends_add_button: "Agregar amigo",
    friends_search_placeholder: "Buscar amigos",
    friends_remove: "Eliminar amigo",
    friends_unfriend: "Eliminar amigo",
    friends_unfriend_confirm: "¿Estás seguro de que quieres eliminar a {name} de tus amigos? Se eliminarán las preferencias específicas.",
    friends_unfriend_confirm_generic: "¿Eliminar a esta persona de tus amigos? Se eliminarán las preferencias específicas.",
    friends_request_sent: "Solicitud de amistad enviada",
    friends_request_accepted: "Solicitud de amistad aceptada",
    friends_request_declined: "Solicitud rechazada",
    friends_toast_connected: "Conectados",
    friends_toast_declined: "Rechazada",
    friends_toast_unfriended: "Amigo eliminado",
    friends_share_title: "¿Qué compartes con {name}?",
    friends_share_title_generic: "¿Qué compartes con esta persona?",
    friends_share_description: "Estas configuraciones anulan tus valores predeterminados globales solo para esta persona",
    friends_save_preferences: "Guardar preferencias para {name}",
    friends_using_global: "Usando configuración global",
    friends_custom_override: "Configuración personalizada",
    friends_field_name: "Nombre",
    friends_field_photo: "Foto de perfil",
    friends_field_email: "Correo",
    friends_field_language: "Idioma",
    friends_field_instagram: "Instagram",
    friends_field_whatsapp: "WhatsApp",
    friends_field_name_long: "Nombre visible",
    friends_field_photo_long: "Foto de perfil",
    friends_field_email_long: "Correo electrónico",
    friends_field_language_long: "Preferencia de idioma",
    friends_field_instagram_long: "Usuario de Instagram",
    friends_field_whatsapp_long: "WhatsApp",
    friends_state_on: "ACTIVADO",
    friends_state_off: "DESACTIVADO",
    friends_state_override_prefix: "Personalizado · global está",
    friends_state_global_prefix: "Global ·",
    friends_reset: "Restablecer",
    friends_done: "Listo",
    friends_close: "Cerrar",

    qr_personal_eyebrow: "Tu QR personal",
    qr_personal_title_signin: "Inicia sesión para conectar",
    qr_personal_body: "Crea una cuenta gratis para obtener tu código QR personal y conectar con amigos.",
    qr_personal_cta: "Iniciar sesión · Crear cuenta",
    qr_share_eyebrow: "Tu QR",
    qr_share_title: "Comparte tu código QR",
    qr_share_scan_label: "Escanear para conectar",
    qr_share_body: "Un amigo lo escanea con el escáner de Habano — recibirás una notificación de solicitud.",

    // Auth + Guest labels
    user_guest: "Invitado",
    user_guest_prompt: "Inicia sesión para acceder a todas las funciones",
    role_user: "Viajero",
    role_vendor: "Vendedor",
    role_business: "Negocio",
    role_admin: "Administrador",
    user_role_traveler: "Viajero",
    user_role_business: "Dueño de Negocio",
    user_role_vendor: "Vendedor",
    auth_log_in: "Crear Cuenta / Iniciar Sesión",
    auth_log_out: "Cerrar Sesión",
    auth_log_out_confirm: "¿Estás seguro de que quieres cerrar sesión?",
    auth_log_out_confirm_body: "Volverás a la pantalla de inicio y deberás iniciar sesión de nuevo para acceder a tu cuenta.",
    auth_guest_tagline: "Guarda lugares, sigue tu viaje, conecta con amigos",
    auth_sign_in: "Iniciar sesión",
    auth_sign_in_google: "Iniciar sesión con Google",
    auth_create_account: "Crear cuenta",
    auth_welcome_back: "Bienvenido de vuelta",
    auth_continue_guest: "Continuar como invitado",
    auth_keep_browsing_guest: "Continuar como invitado",
    auth_already_have_account: "¿Ya tienes una cuenta?",
    auth_no_account: "¿No tienes una cuenta?",
    auth_or_continue_with: "O continuar con",
    auth_agree_terms: "Al continuar aceptas nuestros Términos y Política de Privacidad",
    auth_join_title: "Únete a tu Habana privada",
    auth_join_body: "Crea una cuenta gratuita para {action}hacer check-in, guardar lugares y registrar tu viaje a La Habana.",
    auth_join_body_no_action: "Crea una cuenta gratuita para hacer check-in, guardar lugares y registrar tu viaje a La Habana.",
    join_open_app: "Abrir en la app de Habano",
    join_add_to_home: "Añadir a la pantalla de inicio",
    join_discover_tagline: "Descubre el estilo de vida del tabaco en La Habana con Habano.app",
    join_create_footer: "Crea una cuenta gratuita para hacer check-in, guardar lugares y registrar tu viaje a La Habana.",
    select_role_welcome: "Bienvenido a Habano",
    select_role_pick_title: "Elige cómo usarás",
    select_role_pick_sub: "Elige la opción que mejor te quede. Puedes añadir otro rol más tarde desde Configuración.",
    select_role_traveler_title: "Viajero / Entusiasta",
    select_role_traveler_body: "Descubre los salones de tabaco, vendedores y rincones secretos de La Habana. Registra tu viaje en privado.",
    select_role_pro_title: "Soy un Vendedor o Negocio",
    select_role_pro_body: "Lista tus servicios, recibe check-ins, gana reseñas y crece.",
    select_role_join_vendor: "Únete como Vendedor",
    select_role_join_business: "Únete como Negocio",
    select_role_continue_btn: "Continuar",
    select_role_error_pro: "Selecciona Vendedor o Negocio para continuar",
    select_role_save_error: "No se pudo guardar el rol",
    // Airport v2 — clean two-option layout (ES)
    airport_tab_arriving: "ENTRANDO",
    airport_tab_departing: "SALIENDO",
    // iter179 — Section eyebrows + titles/descriptions (ES).
    airport_arr_step_eyebrow: "Entrando · Paso 1",
    airport_arr_step_desc: "Consigue un viaje desde el aeropuerto a cualquier lugar en La Habana.",
    airport_arr_premium_eyebrow: "Entrando · Premium",
    airport_arr_vip_title: "Llegada VIP",
    airport_arr_vip_desc: "Evita las filas. Traslado privado con servicio de bienvenida.",
    airport_dep_step_eyebrow: "Saliendo · Paso 1",
    airport_dep_step_desc: "Consigue un viaje al aeropuerto desde cualquier lugar en La Habana.",
    airport_dep_premium_eyebrow: "Saliendo · Premium",
    airport_dep_vip_title: "Salida VIP",
    airport_dep_vip_desc: "Traslado privado al aeropuerto. Puntual, siempre.",
    airport_arr_vip_t: "Servicio VIP de Llegada",
    airport_arr_vip_s: "Todo incluido — sin estrés, sin negociación",
    airport_arr_vip_pill_1: "Traslado Privado",
    airport_arr_vip_pill_2: "Bienvenida Personal",
    airport_arr_vip_pill_3: "Asistencia con Equipaje",
    airport_arr_vip_pill_4: "Sin Filas",
    airport_arr_vip_cta: "Obtener Servicio VIP",
    airport_arr_vip_wa: "Hola, estoy llegando al Aeropuerto José Martí y quisiera el servicio VIP de llegada — traslado privado, bienvenida personalizada, asistencia con equipaje y sin filas",
    airport_arr_ride_t: "Solicitar Transporte de Llegada",
    airport_arr_ride_s: "Consigue un transporte desde el aeropuerto hasta tu destino",
    airport_arr_ride_warn: "Negocia el precio antes de subir",
    airport_arr_ride_cta: "Solicitar Transporte de Llegada",
    airport_arr_ride_wa: "Hola, acabo de llegar al Aeropuerto José Martí y necesito un transporte a mi destino a través de Habano.app",
    airport_dep_vip_t: "Acceso al Salón VIP",
    airport_dep_vip_s: "Relájate y fuma antes de tu vuelo",
    airport_dep_vip_pill_1: "Acceso al Salón",
    airport_dep_vip_pill_2: "Lounge de Habanos",
    airport_dep_vip_cta: "Obtener Acceso al Salón VIP",
    airport_dep_vip_wa: "Hola, voy a salir desde el Aeropuerto José Martí y quisiera acceso al salón VIP y al lounge de habanos",
    airport_dep_ride_t: "Solicitar Transporte de Salida",
    airport_dep_ride_s: "Consigue un transporte al aeropuerto desde tu ubicación",
    airport_dep_ride_wa: "Hola, necesito un transporte al Aeropuerto José Martí para mi salida a través de Habano.app",
    // Journey extras (ES)
    j72_vendors_btn: "Mis Vendedores",
    j72_vendors_sub_n: "{n} guardados · conductores, guías y más",
    j72_save_loc_btn: "Guardar una Ubicación",
    j72_save_loc_sub: "Añade un lugar especial a tu mapa",
    // Custom location ride (ES)
    cl_request_ride: "Pedir Transporte Aquí",
    cl_request_ride_wa: "Hola, necesito un transporte a {name}, {address}",
    // Install page (ES)
    install_ios_title: "Agregar a la Pantalla de Inicio",
    install_ios_sub: "Sigue estos pasos para instalar Habano en tu iPhone",
    install_ios_step_1: "Toca el botón Compartir en la parte inferior de Safari — si no lo ves, toca los tres puntos (•••) en la parte inferior derecha de tu pantalla primero",
    install_ios_step_2: "Desplázate hacia abajo y toca Agregar a la pantalla de inicio",
    install_ios_step_3: "Toca Agregar en la esquina superior derecha",
    install_ios_got_it: "Entendido",
    install_android_title: "Instalar Habano",
    install_android_sub: "Agrega Habano a tu pantalla de inicio para acceso instantáneo",
    install_android_cta: "Agregar a la Pantalla de Inicio",
    install_already: "Habano ya está instalado ✓",
    install_unavailable_t: "Agrega Habano a tu Pantalla de Inicio",
    install_unavailable_s: "Abre esta página en Safari (iPhone) o Chrome (Android) para instalar Habano como una app.",
    // Iter 74 — MY VENDORS inline section (ES)
    j74_vendors_eyebrow: "MIS VENDEDORES",
    j74_vendors_see_all: "Ver todo",
    j74_vendor_type_driver: "Conductor",
    j74_vendor_type_tour_guide: "Guía Turístico",
    j74_vendor_type_cigar_roller: "Torcedor",
    j74_vendor_type_artist: "Artista",
    j74_vendor_type_musician: "Músico",
    j74_vendor_type_photographer: "Fotógrafo",
    j74_vendor_type_chef: "Chef",
    j74_vendor_type_default: "Vendedor",
    j74_vendor_whatsapp: "WhatsApp",
    j74_vendor_empty_title: "Mis Vendedores",
    j74_vendor_empty_sub: "Encuentra conductores, guías y más",
    j74_vendor_wa_msg: "Hola {name}, te encontré en Habano.app",
    // Iter 76 — Staying-at ride (ES)
    staying_ride_wa: "Hola, necesito un transporte a {name}, {address}",
    ride_unavailable: "El servicio de transporte no está disponible en este momento",
    // Iter 76 — /tonight page + Saved tabs (ES)
    tonight_eyebrow: "ESTA NOCHE",
    tonight_title: "Esta Noche en La Habana",
    tonight_empty: "No hay lugares abiertos esta noche.",
    saved_tab_all: "Todos",
    saved_tab_businesses: "Negocios",
    saved_tab_vendors: "Vendedores",
    saved_tab_gems: "Tesoros",
    saved_tab_vip: "VIP",
    saved_vip_removed_undo: "Eliminado",
    undo: "Deshacer",
    loading: "Cargando…",
    menu_my_favorites: "Mis Favoritos",
    menu_my_places: "Mis Lugares",
    menu_my_hotel: "Mi Hotel",
    menu_install_app: "Instalar App",
    // iter129 — Get Listed + view selector + applications
    menu_get_listed: "Lista Tu Negocio",
    menu_business_dashboard: "Panel del Negocio",
    menu_vendor_dashboard: "Panel del Vendedor",
    menu_application_pending: "Solicitud Pendiente",
    menu_save_for_offline: "Guardar sin Internet",
    menu_save_for_offline_saving: "Guardando…",
    menu_save_for_offline_done: "Listo sin Internet",
    badge_active: "ACTIVO",
    badge_pending: "PENDIENTE",
    app_pending_review: "Tu solicitud está en revisión — te notificaremos en 48 horas",
    your_businesses: "Tus Negocios",
    view_selector_title: "Elige Tu Vista",
    view_selector_sub: "Puedes cambiar esto en cualquier momento",
    view_tourist_title: "Turista",
    view_tourist_desc: "Para alguien que visita La Habana y quiere descubrir la ciudad — restaurantes, hoteles, traslados al aeropuerto, consejos de viaje y lo mejor de La Habana.",
    view_local_title: "Cubano",
    view_local_desc: "Para residentes de La Habana que quieren acceso rápido a sus lugares favoritos, ubicaciones personalizadas y los sitios cotidianos a los que regresan con frecuencia.",
    view_save_tourist: "Guardar — Vista Turista",
    view_save_local: "Guardar — Vista Cubano",
    // iter147 — Offline & Install page.
    offline_page_title: "Sin Internet e Instalar",
    offline_page_sub: "Elige cómo quieres acceder a Habano sin internet",
    offline_card_save_title: "Guardar La Habana sin Internet",
    offline_card_save_desc: "Descarga tus lugares guardados, mapa, hotel y negocios para que Habano funcione sin internet en Cuba.",
    offline_card_install_title: "Instalar en Tu Dispositivo",
    offline_card_install_desc: "Agrega Habano a tu pantalla de inicio para acceso instantáneo — sin tienda de aplicaciones. Funciona en iPhone y Android.",
    offline_cta_select: "Selecciona una opción",
    offline_cta_save: "Guardar La Habana sin Internet",
    offline_cta_install: "Agregar a Pantalla de Inicio",
    offline_state_saved: "Guardado",
    offline_state_installed: "Instalado",
    offline_state_update: "Actualizar",
    offline_row_link: "Sin Internet e Instalar",
    get_listed_title: "Crecer con Habano",
    get_listed_sub: "Únete a los negocios y vendedores que dan forma a la experiencia habanera",
    get_listed_business_title: "Negocio",
    get_listed_business_sub: "Para negocios con ubicación física en La Habana",
    get_listed_business_sheet_desc: "Para negocios con ubicación física en La Habana — restaurantes, hoteles, lounges de tabacos, azoteas y más.",
    get_listed_business_cta: "Aplicar como Negocio",
    get_listed_business_tags: "Restaurante · Salón de Tabaco · Hotel · Rooftop Bar · Café · Museo",
    get_listed_vendor_title: "Vendedor",
    get_listed_vendor_sub: "Para proveedores de servicios — sin ubicación fija",
    get_listed_vendor_sheet_desc: "Para proveedores de servicios sin ubicación fija — conductores, guías, músicos, fotógrafos y más.",
    get_listed_vendor_cta: "Aplicar como Vendedor",
    get_listed_vendor_tags: "Conductor · Guía Turístico · Músico · Artista · Fotógrafo · Tabaquero",
    get_listed_sheet_sub: "Elige cómo quieres unirte a Habano",
    get_listed_select_hint: "Selecciona una opción para continuar",
    // iter190 — /join marketing page (Haz Crecer Tu Negocio).
    join_page_tag: "Únete a la plataforma",
    join_page_title_line1: "Haz Crecer Tu Negocio Con",
    join_page_title_habano: "Habano",
    join_page_sub: "Conecta con viajeros y locales que descubren lo mejor de La Habana",
    join_why_title: "Por Qué Habano",
    join_why_1_title: "Sé Descubierto",
    join_why_1_body: "Llega a turistas y locales que buscan lo mejor de La Habana",
    join_why_2_title: "Solicitudes de Viaje",
    join_why_2_body: "Los clientes solicitan viajes directamente a tu ubicación",
    join_why_3_title: "Construye tu Reputación",
    join_why_3_body: "Crece con reseñas, calificaciones y acceso VIP",
    join_business_title: "Listar Mi Negocio",
    join_business_desc: "Ubicaciones físicas — restaurantes, bares, cafés, tiendas y más",
    join_business_tags: "Restaurante · Bar · Café · Tabaco · + más",
    join_business_cta: "Listar Mi Negocio",
    join_vendor_title: "Ser Vendedor",
    join_vendor_desc: "Proveedores de servicios — choferes, fotógrafos, guías, artistas",
    join_vendor_tags: "Chofer · Fotógrafo · Guía · Artista",
    join_vendor_cta: "Ser Vendedor",
    join_footer_hint: "Selecciona una opción arriba",
    join_lang_english: "English",
    join_lang_spanish: "Español",
    join_back_aria: "Volver al inicio",
    join_choose_label: "Elige cómo te unes",
    join_add_another_business: "Agregar Otro Negocio",
    join_see_my_business: "Ver Mi Negocio",
    apply_whatsapp_invalid: "El número de WhatsApp no es válido. Incluye el código de país (ej. +5355559999).",
    crop_step_rotate: "Rotar",
    crop_step_id_eyebrow: "Paso 1 de 2",
    crop_step_face_eyebrow: "Paso 2 de 2",
    crop_step_preview: "Vista previa",
    crop_step_submitting: "Subiendo…",
    crop_rotate_label: "Rota si es necesario",
    crop_rotate_next: "Se ve bien",
    crop_step_id_title: "Recorta tu Identificación",
    crop_step_id_sub: "Alinea tu identificación dentro del rectángulo",
    crop_step_face_title: "Recorta tu Rostro",
    crop_step_face_sub: "Centra tu rostro dentro del cuadrado",
    crop_next: "Siguiente",
    crop_preview_title: "¿Se ve bien?",
    crop_preview_id_label: "Identificación",
    crop_preview_face_label: "Tu Rostro",
    crop_preview_redo_id: "Rehacer Identificación",
    crop_preview_redo_face: "Rehacer Rostro",
    crop_submit: "Enviar",
    apply_id_open_crop: "Subir y Recortar ID",
    apply_id_change: "Cambiar ID",
    apply_submit: "Enviar Solicitud",
    apply_submitted_toast: "Solicitud enviada — revisaremos en 48 horas",
    // iter181 — Thank-you screen (ES).
    apply_thanks_title: "¡Gracias!",
    apply_thanks_sub_business: "Tu solicitud de negocio ha sido enviada. Nuestro equipo la revisará y se pondrá en contacto contigo pronto.",
    apply_thanks_sub_vendor: "Tu solicitud de vendedor ha sido enviada. Nuestro equipo revisará tu identificación y se pondrá en contacto contigo pronto.",
    apply_thanks_home_btn: "Volver al Inicio",
    apply_offline_notice: "Estás sin internet — tu solicitud se enviará cuando te conectes",
    field_business_name: "Nombre del negocio",
    field_category: "Categoría",
    field_address: "Dirección",
    field_whatsapp: "Número de WhatsApp",
    field_website: "Sitio web",
    field_instagram: "Instagram",
    field_description: "Descripción",
    field_full_name: "Nombre completo",
    field_service_type: "Tipo de servicio",
    field_languages: "Idiomas que hablas",
    field_years_experience: "Años de experiencia",
    field_brief_description: "Descripción breve",
    apply_max_300: "Máximo 300 caracteres",
    apply_required: "Obligatorio",
    apply_gov_id_label: "Identificación Oficial",
    apply_gov_id_sub: "Tu identificación se almacena de forma segura y solo es visible para nuestro equipo",
    apply_gov_id_button: "Subir Identificación",
    cat_restaurant: "Restaurante",
    cat_paladar: "Paladar",
    cat_cigar_lounge: "Salón de Tabaco",
    cat_hotel: "Hotel",
    cat_rooftop_bar: "Rooftop Bar",
    cat_cafe: "Café",
    cat_bar: "Bar",
    cat_museum: "Museo",
    cat_other: "Otro",
    svc_driver: "Conductor",
    svc_tour_guide: "Guía Turístico",
    svc_musician: "Músico",
    svc_photographer: "Fotógrafo",
    svc_artist: "Artista",
    svc_cigar_roller: "Tabaquero",
    svc_other: "Otro",
    lang_spanish: "Español",
    lang_english: "Inglés",
    lang_french: "Francés",
    lang_german: "Alemán",
    lang_italian: "Italiano",
    lang_russian: "Ruso",
    lang_other: "Otro",
    // Iter 77 — Driver QR modal + map actions (ES)
    driver_qr_option_title: "Mostrar QR al Conductor",
    driver_qr_option_sub: "Muéstraselo a tu conductor para escanear",
    driver_qr_title: "QR para el Conductor",
    driver_qr_brightness_tip: "Consejo: aumenta el brillo de tu pantalla para facilitar el escaneo al aire libre",
    driver_qr_done: "Listo",
    map_request_ride: "Transporte",
    map_open_in_maps: "Abrir en Mapas",
    map_back_to_home: "Volver al Inicio",
    open_apple_sub: "Direcciones nativas de iOS",
    open_google_sub: "Abrir en Google Maps",
    // Iter 81 — Global ride request modal (ES)
    ride_modal_title: "Solicitar un Conductor",
    ride_modal_sub: "Enviaremos tus detalles a un conductor por WhatsApp",
    ride_modal_review_title: "Revisa tu solicitud",
    ride_pickup: "RECOGIDA",
    ride_destination: "DESTINO",
    ride_passengers: "PASAJEROS",
    ride_luggage: "EQUIPAJE",
    ride_pickup_loading: "Obteniendo tu ubicación…",
    ride_pickup_ok: "Ubicación capturada ✓",
    ride_pickup_tap: "Toca para escribir la dirección",
    ride_pickup_manual_hint: "Dirección manual",
    ride_review_send: "Revisar y Enviar",
    ride_edit: "Editar",
    ride_send: "Enviar",
    ride_qr_offline_note: "El QR para el conductor funciona sin internet",
    ride_request_driver: "Solicitar un Viaje",
    ride_request_driver_to_hotel: "Solicitar un Viaje al Hotel",
    ride_get_qr: "Ver Direcciones",
    ride_qr_works_offline: "Funciona sin internet",
    ride_phone_contact: "Llamar al negocio",
    ride_send_btn: "Solicitar Auto Ahora",
    ride_view_directions: "Ver Direcciones",
    ride_share_location: "Compartir Lugar",
    ride_open_apple_maps: "Abrir en Apple Maps",
    ride_open_google_maps: "Abrir en Google Maps",
    ride_qr_tap_caption: "Toca el QR para abrir en Google Maps",
    contact_btn: "Contactar",
    contact_vendor_btn: "Contactar Vendedor",
    track_spend_title: "Registrar Gasto",
    track_spend_sub: "Anota lo que gastas aquí",
    log_spend_modal_title: "Registrar Tu Gasto",
    log_spend_modal_disclaimer: "Este es tu registro personal — no se comparte con el negocio",
    log_spend_amount_placeholder: "¿Cuánto gastaste aquí?",
    log_spend_save: "Guardar",
    log_spend_remove_confirm: "¿Quitar el seguimiento de gastos para este negocio?",
    admin_log_spend_label: "Registrar Gasto",
    admin_log_spend_on: "Registrar Gasto activo — los usuarios pueden anotar lo que gastan",
    admin_log_spend_off: "Registrar Gasto desactivado — el seguimiento está oculto",
    heart_hint_save: "Guardar para después",
    // Driver QR enhancements (ES)
    driver_qr_tap_open: "Toca el QR para abrir en Google Maps",
    driver_qr_offline_card: "Funciona sin internet — no necesitas conexión para mostrar el QR al conductor",
    driver_qr_save: "Guardar en Fotos",

    // iter101 — Connect page rebuild + dedicated section pages (ES).
    connect_page_title: "Conectar",
    connect_scan_title: "Escanea cualquier QR de Habano",
    connect_scan_sub: "Negocios, vendedores o amigos",
    connect_scan_cta: "Toca para Escanear",
    connect_show_my_qr: "Mostrar Mi QR",
    connect_more: "MÁS",
    connect_tap_to_view: "toca para ver →",
    connect_box_friends_label: "AMIGOS",
    connect_box_friends_action: "+ Agregar Amigo",
    connect_box_places_label: "MIS LUGARES",
    connect_box_places_action: "+ Guardar Lugar",
    connect_box_vendors_label: "VENDEDORES",
    connect_box_vendors_action: "Escanear Vendedor",
    connect_box_favorites_label: "FAVORITOS",
    connect_box_favorites_action: "Descubrir Más",

    // iter103 — Google-only LoginSheet mode + admin WA toggle (ES).
    login_google_only_title: "Inicia sesión en Habano",
    login_google_only_sub: "Usa tu cuenta de Google para continuar",
    admin_settings_auth_section: "Autenticación",
    admin_settings_wa_toggle_label: "Inicio de sesión con WhatsApp",
    admin_settings_wa_on_label: "WhatsApp activo — los usuarios pueden crear cuentas con WhatsApp",
    admin_settings_wa_off_label: "WhatsApp desactivado — solo Google",
    admin_settings_wa_helper: "Cuando está desactivado, los invitados pueden seguir usando WhatsApp para contactar negocios y pedir viajes — solo no pueden crear cuentas con WhatsApp.",

    // Dedicated section pages (ES).
    page_friends_title: "Amigos",
    page_friends_empty: "Aún no tienes amigos — escanea el QR de un amigo para conectarte",
    page_friends_pending_banner: "{n} solicitud pendiente",
    page_friends_pending_banner_plural: "{n} solicitudes pendientes",
    friends_bar_add: "Agregar Amigo",
    friends_bar_my_qr: "Mi QR",
    friends_show_qr: "Mostrar Código QR",
    friends_qr_title_my: "Mi Código QR",
    friends_qr_title_friend: "QR de {name}",
    friends_qr_caption_my: "Pide a tu amigo que escanee este código",
    friends_qr_caption_friend: "Comparte esto con {name} o escanea para conectar",
    friends_qr_share_link: "Compartir Enlace",
    friends_qr_save: "Guardar QR",
    friends_badge_tourist: "Turista",
    friends_badge_local: "Cubano",
    friends_scan_camera_title: "Escanea el QR de un amigo",
    friends_scan_add_confirm_title: "¿Agregar a {name}?",
    friends_scan_add_confirm_cancel: "Cancelar",
    friends_scan_add_confirm_confirm: "Agregar Amigo",
    friends_toast_added: "Amigo agregado",
    friends_qr_share_text: "Agrégame en Habano",

    page_my_places_title: "Mis Lugares",
    page_my_places_empty: "Aún no hay lugares guardados — guarda una ubicación para construir tu mapa personal de La Habana",
    page_my_places_save_cta: "Guardar un Lugar",

    page_my_vendors_title: "Mis Vendedores",
    page_my_vendors_empty: "Aún no hay vendedores guardados — escanea el QR de un vendedor para agregarlo",
    page_my_vendors_chat: "Chat",

    page_my_favorites_title: "Favoritos",
    page_my_favorites_empty: "Aún no hay favoritos — toca el corazón en un negocio para guardarlo aquí",
  },
};

const I18nContext = createContext({ t: (k) => k, lang: "en", setLang: () => {} });

/**
 * Persist the language preference to the user record when logged in.
 * Fire-and-forget — never blocks UI.
 */
async function syncLangToProfile(lang) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/me`;
    await axios.patch(url, { language: lang }, { withCredentials: true });
  } catch { /* ignore — customer users / network errors are fine */ }
}

export function I18nProvider({ children }) {
  // Boot order: localStorage → default "es" (iter76 — Spanish-first launch).
  // `me.language` from /auth/me is applied later in the useEffect below once
  // we have a session.
  const [lang, setLangState] = useState(() => lsGet("habano.lang") || "es");
  const [overrides, setOverrides] = useState({ en: {}, es: {} });

  const setLang = (next) => {
    if (next !== "en" && next !== "es") return;
    setLangState(next);
    lsSet("habano.lang", next);
    document.documentElement.lang = next;
    syncLangToProfile(next);
    // iter155 — Broadcast so downstream hooks (weather unit, mode
    // screen, etc.) can react without waiting for a full re-render.
    try { window.dispatchEvent(new CustomEvent("habano:lang-changed", { detail: next })); } catch { /* ignore */ }
  };

  useEffect(() => {
    lsSet("habano.lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // Pull the saved language from /auth/me — only override the local choice
  // when the user has explicitly never set a localStorage value.
  useEffect(() => {
    const meUrl = `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`;
    axios.get(meUrl, { withCredentials: true }).then((res) => {
      const profileLang = res?.data?.language;
      const stored = lsGet("habano.lang");
      if (profileLang && (profileLang === "en" || profileLang === "es") && !stored) {
        setLangState(profileLang);
        lsSet("habano.lang", profileLang);
      }
    }).catch(() => { /* not logged in */ });
  }, []);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/translations`;
    axios.get(url).then((res) => {
      if (res.data && typeof res.data === "object") setOverrides({ en: res.data.en || {}, es: res.data.es || {} });
    }).catch(() => {});
  }, []);

  const t = (k) =>
    (overrides[lang] && overrides[lang][k]) ||
    dict[lang]?.[k] ||
    (overrides.en && overrides.en[k]) ||
    dict.en[k] ||
    k;

  return <I18nContext.Provider value={{ t, lang, setLang }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
